import React, { useEffect, useMemo, useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Row, 
  Col, 
  Statistic, 
  Spin,
  Tabs,
  Drawer,
  Modal,
  Form,
  InputNumber,
  
  message,
  Typography,
  DatePicker,
  Progress,
  Tooltip
} from 'antd';
import { 
  UploadOutlined,
  
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CarOutlined,
  RocketOutlined,
  SendOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApplicationCell, DataTable, ExternalApplicationCell, MoneyCell, StatusCell } from '../../ui';
import dayjs from 'dayjs';
import { workspaceApplicationNumberLines, WorkspaceTwoLineTableCell } from '../../components/workspace/WorkspaceTasks';
import { 
  useGetApplicationsFinanceListQuery, 
  useGetApplicationFinanceQuery,
  useGetFinanceStatisticsQuery,
  useUpdatePrimaryDocsStatusMutation,
  useUpdatePartnerPaymentStatusMutation,
  useCreatePartnerPaymentMutation,
  useGetRoutesReadModelQuery,
  useGetPaymentsListQuery,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
  useGetAvailableRoutesForPaymentsQuery,
  useGetRouteObligationsForPaymentQuery,
  useUploadLoadingFinanceDocsMutation,
  useUploadTransportFinanceDocsMutation,
  useSetPartnerReceivableMutation,
  useUploadApplicationPrimaryDocsMutation,
  useUpdateRouteIncomeMutation,
  Payment,
} from '../../store/api/financeApi';
import { useGetUsersQuery } from '../../store/api/usersApi';
import {
  filterResponsibleEligibleUsers,
  formatResponsibleUserLabel,
} from '../../utils/userDisplayName';
import { useGetCarriersQuery } from '../../store/api/referencesApi';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import UploadDocumentModal from '../../components/documents/UploadDocumentModal';
import {
  downloadDocument,
  documentPreviewEndpoint,
  fetchDocumentObjectUrl,
  resolveDocumentFilename,
} from '../../utils/documentDownload';
import { useGetDocumentsByRouteQuery } from '../../store/api/documentsApi';
import { useStartPaymentCounterMutation, useMarkPaymentPaidMutation, useUpdateClientPaymentsMutation } from '../../store/api/applicationsApi';
import { formatCurrency } from '../../utils/dateUtils';
import { getRegistryPaymentTypeLabel, getRegistryPaymentTypeTagColor } from '../../utils/paymentTypeLabels';
import { useGetRouteByIdQuery } from '../../store/api/routesApi';
import RouteDetailsModal from '../../components/routes/RouteDetailsModal';
import { obligationStageLabel, obligationStageToPaymentType } from '../../utils/obligationStageUi';
import { financeDirectionLabel } from '../../utils/enumUiLabels';
import GovernanceRiskHeader from '../../components/governance/GovernanceRiskHeader';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/** Основание PARTNER_CONTRACT — партнёрский поток (вместо устаревшего isCompanyApplication). */
const isPartnerBasisApplication = (a: any) => a?.applicationBasis === 'PARTNER_CONTRACT';
/** Клиентский поток: любое основание, кроме PARTNER_CONTRACT (в т.ч. null — как после миграции). */
const isClientFlowApplication = (a: any) => !isPartnerBasisApplication(a);

const FinancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('routes');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [form] = Form.useForm();

  // Состояние для маршрутов
  const [routesPage, setRoutesPage] = useState(1);
  const [routesStatus, setRoutesStatus] = useState('ALL');
  const [routesTransportType, setRoutesTransportType] = useState('ALL');
  const [routesSearch, setRoutesSearch] = useState('');
  const [routesApplication, setRoutesApplication] = useState('ALL');
  const [routesCompanyOnly, setRoutesCompanyOnly] = useState(false);
  const [routesResponsibleLogistId, setRoutesResponsibleLogistId] = useState<number | undefined>(undefined);
  const [routesCarrierId, setRoutesCarrierId] = useState<number | undefined>(undefined);
  const [routesTableY, setRoutesTableY] = useState<number>(() => Math.max(420, window.innerHeight - 360));

  // Состояние для вкладки "Логисты" (выбранный логист)
  const [selectedLogistId, setSelectedLogistId] = useState<number | undefined>(undefined);

  // Состояние для платежей
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsStatus, setPaymentsStatus] = useState('ALL');
  const [paymentsType, setPaymentsType] = useState('ALL');
  const [paymentsCompanyOnly, setPaymentsCompanyOnly] = useState(false);
  const [isCreatePaymentModalVisible, setIsCreatePaymentModalVisible] = useState(false);
  const [isUpdatePaymentModalVisible, setIsUpdatePaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [paymentForm] = Form.useForm();
  const [updatePaymentForm] = Form.useForm();

  // Состояние для загрузки документов
  const [isUploadLoadingDocsModalVisible, setIsUploadLoadingDocsModalVisible] = useState(false);
  const [isUploadTransportDocsModalVisible, setIsUploadTransportDocsModalVisible] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  
  // Состояние для редактирования дохода маршрута
  const [isEditRouteIncomeModalVisible, setIsEditRouteIncomeModalVisible] = useState(false);
  const [selectedRouteForIncomeEdit, setSelectedRouteForIncomeEdit] = useState<any>(null);
  const [incomeForm] = Form.useForm();
  // local caches for upload previews (currently unused) — keep state for future UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingDocumentsData, setLoadingDocumentsData] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [transportDocumentsData, setTransportDocumentsData] = useState<any[]>([]);
  const [selectedRouteForUpload, setSelectedRouteForUpload] = useState<any>(null);
  const [documentsRouteId, setDocumentsRouteId] = useState<number | null>(null);
  const [applicationIdForRoutes, setApplicationIdForRoutes] = useState<number | null>(null);
  const [applicationInternalNumberForRoutes, setApplicationInternalNumberForRoutes] = useState<string | null>(null);
  const [isRoutesDrawerOpen, setIsRoutesDrawerOpen] = useState(false);

  const { data: applicationsData, isLoading: isLoadingApplications, refetch: refetchApplications, error: applicationsError } = useGetApplicationsFinanceListQuery({
    page,
    limit: pageSize,
    status: status === 'ALL' ? undefined : status,
    search: search || undefined
  });

  // Отладочная информация
  console.log('🔍 FinancePage Debug:');
  console.log('- isLoadingApplications:', isLoadingApplications);
  console.log('- applicationsError:', applicationsError);
  console.log('- applicationsData:', applicationsData);
  
  const { data: statistics, isLoading: isLoadingStats, refetch: refetchStats } = useGetFinanceStatisticsQuery({});
  const [setPartnerReceivable] = useSetPartnerReceivableMutation();

  // Деталка по заявке (маршруты внутри заявки)
  const { data: applicationFinanceDetail, isFetching: isFetchingApplicationDetail } = useGetApplicationFinanceQuery(
    applicationIdForRoutes as number,
    { skip: !applicationIdForRoutes }
  );

  // Фолбэк: если детальная заявка по какой-то причине не вернет маршруты,
  // запрашиваем маршруты по фильтру application (internalNumber)
  const { data: routesByApplication, isLoading: isLoadingRoutesByApplication } = useGetRoutesReadModelQuery(
    {
      page: 1,
      limit: 200,
      application: applicationInternalNumberForRoutes || undefined,
    },
    { skip: !applicationInternalNumberForRoutes }
  );

  // Запрос данных маршрутов
  const { data: routesData, isLoading: isLoadingRoutes, refetch: refetchRoutes, error: routesError } = useGetRoutesReadModelQuery({
    page: routesPage,
    limit: 20,
    status: routesStatus === 'ALL' ? undefined : routesStatus,
    transportType: routesTransportType === 'ALL' ? undefined : routesTransportType,
    search: routesSearch || undefined,
    application: routesApplication === 'ALL' ? undefined : routesApplication,
    companyOnly: routesCompanyOnly || undefined,
    responsibleLogistId: routesResponsibleLogistId,
    carrierId: routesCarrierId,
  });

  // Маршруты выбранного логиста для вкладки "Логисты"
  const { data: logistsRoutesData, isLoading: isLoadingLogistsRoutes } = useGetRoutesReadModelQuery(
    {
      page: 1,
      limit: 200,
      responsibleLogistId: selectedLogistId,
    },
    {
      skip: !selectedLogistId,
    }
  );

  const { data: usersData } = useGetUsersQuery();
  const { data: carriersData } = useGetCarriersQuery();

  // Деталка маршрута из финансов по клику на строку
  const [routeDetailsId, setRouteDetailsId] = useState<number | null>(null);
  const [isRouteDetailsVisible, setIsRouteDetailsVisible] = useState(false);
  const { data: routeDetailsData, isFetching: isFetchingRouteDetails } = useGetRouteByIdQuery(routeDetailsId as number, {
    skip: !isRouteDetailsVisible || !routeDetailsId
  });

  // Делаем таблицу маршрутов почти на весь экран (внутренний скролл таблицы)
  useEffect(() => {
    const update = () => setRoutesTableY(Math.max(420, window.innerHeight - 360));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Запрос данных платежей
  const { data: paymentsData, isLoading: isLoadingPayments, refetch: refetchPayments } = useGetPaymentsListQuery({
    page: paymentsPage,
    limit: 20,
    status: paymentsStatus === 'ALL' ? undefined : paymentsStatus,
    paymentType: paymentsType === 'ALL' ? undefined : paymentsType,
    routeId: selectedRouteForUpload?.id || undefined,
    companyOnly: paymentsCompanyOnly || undefined
  });

  // Запрос доступных маршрутов для создания платежей
  const { data: availableRoutes, isLoading: isLoadingAvailableRoutes } = useGetAvailableRoutesForPaymentsQuery();
  const routeIdForObligations = Form.useWatch('routeId', paymentForm) ?? null;
  const paymentObligationDirection = Form.useWatch('paymentObligationDirection', paymentForm) as
    | 'INCOME'
    | 'EXPENSE'
    | undefined;
  const { data: routeObligations = [] } = useGetRouteObligationsForPaymentQuery(routeIdForObligations ?? 0, {
    skip: !routeIdForObligations || !isCreatePaymentModalVisible,
  });
  const routeObligationsFiltered = useMemo(() => {
    if (!paymentObligationDirection) return [];
    return routeObligations.filter((o: any) => o.direction === paymentObligationDirection);
  }, [routeObligations, paymentObligationDirection]);

  // Мутации для платежей
  const [createPayment] = useCreatePaymentMutation();
  const [updatePayment] = useUpdatePaymentMutation();
  const [deletePayment] = useDeletePaymentMutation();
  const [createPartnerPayment] = useCreatePartnerPaymentMutation();

  // Мутации/запросы документов финансового модуля
  const [uploadLoadingFinanceDocs] = useUploadLoadingFinanceDocsMutation();
  const [uploadTransportFinanceDocs] = useUploadTransportFinanceDocsMutation();
  const [updateRouteIncome] = useUpdateRouteIncomeMutation();
  const { data: routeDocuments = [], isFetching: isFetchingRouteDocuments, refetch: refetchRouteDocuments } = useGetDocumentsByRouteQuery(
    documentsRouteId as number,
    { skip: !documentsRouteId }
  );
  const loadingDocs = (routeDocuments || []).filter((d: any) => d.stage === 'loading');
  const transportDocs = (routeDocuments || []).filter((d: any) => d.stage === 'unloading');

  // Вспомогательные загрузчики документов через общий documents API для получения filePath
  // Здесь предположим, что у нас есть endpoint /documents/upload, как в RouteDocumentsModal
  const uploadFileAndGetPath = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    // Минимальный набор метаданных, чтобы файл сохранился и вернул id/filename
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
    const response = await fetch(`${API_BASE_URL}/documents/upload`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : ''
      },
      body: formData,
    });
    if (!response.ok) throw new Error('Upload failed');
    const json = await response.json();
    // backend returns { success, data: { id, filePath, ... } }
    return json?.data?.filePath as string;
  };

  // Загрузка первичных документов заявки: принимает FileList, сохраняет файлы в documents.upload и отдаёт пути на сервер
  const [uploadApplicationPrimaryDocs] = useUploadApplicationPrimaryDocsMutation();
  // uploadPrimaryDocsForApplication: helper to upload files and attach to application
  // currently unused from FinancePage but kept for future UI; keep implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const uploadPrimaryDocsForApplication = async (applicationId: number, files: File[]) => {
    const paths: string[] = [];
    for (const f of files) {
      const p = await uploadFileAndGetPath(f);
      paths.push(p);
    }
    await uploadApplicationPrimaryDocs({ applicationId, files: paths }).unwrap();
  };

  // Отладочная информация для маршрутов
  console.log('- isLoadingRoutes:', isLoadingRoutes);
  console.log('- routesError:', routesError);
  console.log('- routesData:', routesData);

  const [updatePrimaryDocsStatus] = useUpdatePrimaryDocsStatusMutation();
  const [updatePartnerPaymentStatus] = useUpdatePartnerPaymentStatusMutation();
  const [startPaymentCounter] = useStartPaymentCounterMutation();
  const [markPaymentPaid] = useMarkPaymentPaidMutation();
  const [updateClientPayments] = useUpdateClientPaymentsMutation();

  // Состояние для модального окна запуска счётчика
  const [counterModalVisible, setCounterModalVisible] = useState(false);
  const [counterModalType, setCounterModalType] = useState<'prepayment' | 'main'>('prepayment');
  const [counterModalAppId, setCounterModalAppId] = useState<number | null>(null);
  const [counterForm] = Form.useForm();

  // Открытие модального окна для запуска счётчика
  const openCounterModal = (applicationId: number, counterType: 'prepayment' | 'main') => {
    setCounterModalAppId(applicationId);
    setCounterModalType(counterType);
    counterForm.resetFields();
    counterForm.setFieldsValue({
      startDate: dayjs(),
      condition: counterType === 'prepayment' ? 'INVOICE_RECEIVED' : 'ON_UNLOADING'
    });
    setCounterModalVisible(true);
  };

  // Обработчик сохранения из модального окна
  const handleCounterModalSave = async () => {
    try {
      const values = await counterForm.validateFields();
      if (!counterModalAppId) return;

      await startPaymentCounter({
        id: counterModalAppId,
        counterType: counterModalType,
        condition: values.condition,
        startDate: values.startDate?.format('YYYY-MM-DD'),
        invoiceNumber: values.invoiceNumber,
        otherCondition: values.otherCondition,
      }).unwrap();

      message.success(counterModalType === 'prepayment' ? 'Счётчик предоплаты запущен' : 'Счётчик доплаты запущен');
      setCounterModalVisible(false);
      refetchApplications();
    } catch (error: any) {
      console.error('Ошибка при запуске счётчика:', error);
      message.error(error?.data?.error || 'Ошибка при запуске счётчика');
    }
  };

  const handleMarkPaid = async (applicationId: number, paymentType: 'prepayment' | 'main') => {
    try {
      await markPaymentPaid({ id: applicationId, paymentType }).unwrap();
      message.success(paymentType === 'prepayment' ? 'Предоплата отмечена как оплаченная' : 'Доплата отмечена как оплаченная');
      refetchApplications();
    } catch (error: any) {
      console.error('Ошибка при отметке оплаты:', error);
      message.error(error?.data?.error || 'Ошибка при отметке оплаты');
    }
  };

  // handlePrimaryDocsStatusChange kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePrimaryDocsStatusChange = async (applicationId: number, status: 'SIGNED' | 'NOT_SIGNED') => {
    try {
      await updatePrimaryDocsStatus({ applicationId, status }).unwrap();
      message.success('Статус документов обновлен');
      refetchApplications();
    } catch (error) {
      console.error('Ошибка при обновлении статуса документов:', error);
      message.error('Ошибка при обновлении статуса документов');
    }
  };

  // handlePartnerPaymentStatusChange kept for future UI integration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePartnerPaymentStatusChange = async (applicationId: number, status: 'RECEIVED' | 'NOT_RECEIVED', amount?: number) => {
    try {
      await updatePartnerPaymentStatus({ applicationId, status, amount }).unwrap();
      message.success('Статус платежа обновлен');
      refetchApplications();
    } catch (error) {
      console.error('Ошибка при обновлении статуса платежа:', error);
      message.error('Ошибка при обновлении статуса платежа');
    }
  };

  // UploadDocumentModal state (re-use existing modal)
  // remove UploadDocumentModal integration (restore previous behavior)
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadModalApplication, setUploadModalApplication] = useState<any | null>(null);
  const [uploadModalRoute, setUploadModalRoute] = useState<any | null>(null);

  const handleRefresh = () => {
    refetchStats();
    refetchApplications();
    refetchRoutes();
    message.success('Данные обновлены');
  };

  const handleEditRouteIncome = (route: any) => {
    setSelectedRouteForIncomeEdit(route);
    incomeForm.setFieldsValue({ income: route.income });
    setIsEditRouteIncomeModalVisible(true);
  };

  const handleUpdateRouteIncome = async (values: any) => {
    try {
      await updateRouteIncome({
        routeId: selectedRouteForIncomeEdit.id,
        income: values.income
      }).unwrap();
      
      message.success('Доход маршрута обновлен');
      setIsEditRouteIncomeModalVisible(false);
      incomeForm.resetFields();
      refetchRoutes();
    } catch (error) {
      message.error('Ошибка при обновлении дохода маршрута');
    }
  };

  const handleUpdateApplication = (application: any) => {
    setSelectedApplication(application);
    form.setFieldsValue({
      primaryDocsStatus: application.primaryDocsStatus || 'NOT_SIGNED',
      // Для партнёрских заявок
      partnerPaymentStatus: application.partnerPaymentStatus,
      partnerPaymentAmount: application.partnerPaymentAmount,
      partnerReceived: application.financialSummary?.partnerReceived || 0,
      // Для своих заявок
      prepaymentStatus: application.prepaymentStatus || 'NOT_PAID',
      prepaymentReceivedAmount: application.prepaymentReceivedAmount || null,
      prepaymentReceivedDate: application.prepaymentReceivedDate ? dayjs(application.prepaymentReceivedDate) : null,
      mainPaymentStatus: application.mainPaymentStatus || 'NOT_PAID',
      mainPaymentReceivedAmount: application.mainPaymentReceivedAmount || null,
      mainPaymentReceivedDate: application.mainPaymentReceivedDate ? dayjs(application.mainPaymentReceivedDate) : null
    });
    setIsUpdateModalVisible(true);
  };

  const handleCreatePaymentForRoute = (route: any) => {
    setIsCreatePaymentModalVisible(true);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({ routeId: route.id, paymentObligationDirection: 'INCOME' });
  };

  const handleUpdateSubmit = async (values: any) => {
    try {
      const partnerFlow = isPartnerBasisApplication(selectedApplication);

      // Обновляем статус первичных документов
      await updatePrimaryDocsStatus({
        applicationId: selectedApplication.id,
        status: values.primaryDocsStatus,
        signedDate: values.primaryDocsSignedDate?.toISOString()
      }).unwrap();

      // Платёж от партнёра — только для заявок по договору с партнёром
      if (partnerFlow) {
        if (values.partnerPaymentStatus === 'RECEIVED') {
          await updatePartnerPaymentStatus({
            applicationId: selectedApplication.id,
            status: values.partnerPaymentStatus,
            amount: values.partnerPaymentAmount,
            paymentDate: values.partnerPaymentDate?.toISOString()
          }).unwrap();
        } else if (typeof values.partnerPaymentAmount === 'number' && !isNaN(values.partnerPaymentAmount)) {
          await setPartnerReceivable({ applicationId: selectedApplication.id, amount: values.partnerPaymentAmount }).unwrap();
        }

        if (typeof values.partnerReceived === 'number' && values.partnerReceived > 0) {
          try {
            await createPartnerPayment({
              applicationId: selectedApplication.id,
              amount: values.partnerReceived,
              paymentDate: values.partnerPaymentDate?.toISOString(),
              note: values.partnerPaymentInvoice || undefined
            }).unwrap();
          } catch (e) {
            console.error('Ошибка создания поступления от партнёра', e);
          }
        }
      }

      if (!partnerFlow) {
        await updateClientPayments({
          id: selectedApplication.id,
          prepaymentStatus: values.prepaymentStatus,
          prepaymentReceivedAmount: values.prepaymentReceivedAmount,
          prepaymentReceivedDate: values.prepaymentReceivedDate?.format('YYYY-MM-DD'),
          mainPaymentStatus: values.mainPaymentStatus,
          mainPaymentReceivedAmount: values.mainPaymentReceivedAmount,
          mainPaymentReceivedDate: values.mainPaymentReceivedDate?.format('YYYY-MM-DD'),
        }).unwrap();
      }

      message.success('Заявка успешно обновлена');
      setIsUpdateModalVisible(false);
      form.resetFields();
      refetchApplications();
    } catch (error) {
      message.error('Ошибка при обновлении заявки');
    }
  };

  // Функции для работы с платежами
  const handleCreatePayment = () => {
    setIsCreatePaymentModalVisible(true);
    paymentForm.resetFields();
  };

  const handleCreatePaymentSubmit = async (values: any) => {
    try {
      const selectedRoute = availableRoutes?.find((route) => route.id === values.routeId);
      if (!values.obligationId) {
        message.error('Выберите обязательство');
        return;
      }
      const obl = routeObligations.find((o: any) => o.id === values.obligationId);
      if (!obl) {
        message.error('Обязательство не найдено');
        return;
      }

      const payload: any = {
        amount: values.amount,
        paymentDate: values.paymentDate?.toISOString?.(),
        obligationId: values.obligationId,
        routeId: values.routeId ?? obl?.routeId,
        applicationId: obl?.applicationId ?? selectedRoute?.applications?.[0]?.id,
        paymentType: obligationStageToPaymentType(obl?.stage),
      };

      await createPayment(payload).unwrap();

      message.success('Платеж успешно создан');
      setIsCreatePaymentModalVisible(false);
      paymentForm.resetFields();
      refetchPayments();
    } catch (error) {
      message.error('Ошибка при создании платежа');
    }
  };

  // Создать и сразу провести платеж (установить amountPaid = amount и статус)
  const handleCreateAndProcessPayment = async () => {
    try {
      const values = await paymentForm.validateFields();

      if (!values.obligationId) {
        message.error('Выберите обязательство');
        return;
      }
      const obl = routeObligations.find((o: any) => o.id === values.obligationId);
      if (!obl) {
        message.error('Обязательство не найдено');
        return;
      }

      const selectedRoute = availableRoutes?.find((route) => route.id === values.routeId);
      const payload: any = {
        amount: values.amount,
        paymentDate: values.paymentDate?.toISOString?.(),
        obligationId: values.obligationId,
        routeId: values.routeId ?? obl?.routeId,
        applicationId: obl?.applicationId ?? selectedRoute?.applications?.[0]?.id,
        paymentType: obligationStageToPaymentType(obl?.stage),
      };

      Modal.confirm({
        title: 'Провести платеж?',
        content: `Сумма: ${values.amount}. Дата: ${values.paymentDate ? values.paymentDate.format('DD.MM.YYYY') : '—'}. Продолжить?`,
        okText: 'Провести',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await createPayment(payload).unwrap();

            message.success('Платеж проведен');
            setIsCreatePaymentModalVisible(false);
            paymentForm.resetFields();
            refetchPayments();
          } catch (err: any) {
            console.error('Error creating/processing payment:', err);
            // Try to extract server message from RTK Query error shape
            const serverMsg = err?.data?.error || err?.error || (err?.data && typeof err.data === 'string' ? err.data : null);
            message.error(serverMsg ? `Не удалось провести платеж: ${serverMsg}` : 'Не удалось провести платеж');
          }
        }
      });
    } catch (error) {
      // ошибки валидации формы
    }
  };

  const handleUpdatePayment = (payment: Payment) => {
    setSelectedPayment(payment);
    updatePaymentForm.setFieldsValue({
      amountPaid: payment.amountPaid,
      paymentDate: payment.paymentDate ? dayjs(payment.paymentDate) : undefined
    });
    setIsUpdatePaymentModalVisible(true);
  };

  const handleDeletePayment = (payment: Payment) => {
    Modal.confirm({
      title: `Удалить платеж #${payment.id}?`,
      content: 'Действие необратимо. Продолжить?',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deletePayment(payment.id).unwrap();
          message.success('Платеж удален');
          refetchPayments();
          refetchRoutes();
        } catch (e: any) {
          message.error(e?.data?.error || 'Ошибка удаления платежа');
        }
      }
    });
  };

  const handleUpdatePaymentSubmit = async (values: any) => {
    if (!selectedPayment) return;

    Modal.confirm({
      title: 'Подтвердите изменение платежа',
      content: `Установить оплачено: ${values.amountPaid}${values.paymentDate ? `, дата: ${values.paymentDate.format('DD.MM.YYYY')}` : ''}?`,
      okText: 'Применить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await updatePayment({
            id: selectedPayment.id,
            data: {
              amountPaid: values.amountPaid,
              paymentDate: values.paymentDate?.toISOString()
            }
          }).unwrap();

          message.success('Платеж успешно обновлен');
          setIsUpdatePaymentModalVisible(false);
          updatePaymentForm.resetFields();
          setSelectedPayment(null);
          refetchPayments();
        } catch (error) {
          message.error('Ошибка при обновлении платежа');
        }
      }
    });
  };

  // Функция для загрузки документов погрузки
  const handleUploadLoadingDocuments = async (files: FileList) => {
    try {
      if (!selectedRouteForUpload) {
        message.error('Маршрут не выбран');
        return;
      }
      setIsLoadingDocuments(true);

      // Загружаем файлы и собираем пути
      const fileArray = Array.from(files);
      const photoPaths: string[] = [];
      let invoicePath: string | null = null;

      for (const file of fileArray) {
        const path = await uploadFileAndGetPath(file);
        // Простая эвристика: изображения считаем фото, остальные — счёт
        if (/(jpg|jpeg|png|gif|webp)$/i.test(file.name)) {
          photoPaths.push(path);
        } else {
          // Берём первый не-Image как счёт
          if (!invoicePath) invoicePath = path;
        }
      }

      // Дата погрузки: используем текущую, либо добавьте поле формы при необходимости
      const loadingDate = new Date().toISOString();

      await uploadLoadingFinanceDocs({
        routeId: selectedRouteForUpload.id,
        loadingDate,
        photos: photoPaths,
        invoiceDocument: invoicePath || undefined,
      }).unwrap();

      message.success('Документы погрузки успешно загружены');
      setIsUploadLoadingDocsModalVisible(false);
      setSelectedRouteForUpload(null);

      // Обновляем список
      refetchRoutes();
      // Ждём один тик, чтобы бэкенд успел вернуть обновлённые индикаторы
      setTimeout(() => {
        if (documentsRouteId === selectedRouteForUpload.id) refetchDocuments();
      }, 200);
      if (documentsRouteId === selectedRouteForUpload.id) {
        refetchDocuments();
      }
    } catch (error) {
      console.error(error);
      message.error('Ошибка при загрузке документов');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  // Функция для загрузки транспортных документов
  const handleUploadTransportDocuments = async (files: FileList) => {
    try {
      if (!selectedRouteForUpload) {
        message.error('Маршрут не выбран');
        return;
      }
      setIsLoadingDocuments(true);

      const documentPaths: string[] = [];
      for (const file of Array.from(files)) {
        const path = await uploadFileAndGetPath(file);
        documentPaths.push(path);
      }

      // Значения по умолчанию; в модалке уже есть поля выбора — при необходимости их связать
      const deliveryType: 'MAIL' | 'EDO' | 'PERSONAL' = 'MAIL';
      const trackingNumber = undefined;
      const receivedDate = new Date().toISOString();

      await uploadTransportFinanceDocs({
        routeId: selectedRouteForUpload.id,
        deliveryType,
        trackingNumber,
        receivedDate,
        documents: documentPaths,
      }).unwrap();

      message.success('Транспортные документы успешно загружены');
      setIsUploadTransportDocsModalVisible(false);
      setSelectedRouteForUpload(null);

      refetchRoutes();
      setTimeout(() => {
        if (documentsRouteId === selectedRouteForUpload.id) refetchDocuments();
      }, 200);
    } catch (error) {
      console.error(error);
      message.error('Ошибка при загрузке документов');
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  // Функция для обновления списка документов
  const refetchDocuments = () => {
    setIsLoadingDocuments(true);
    if (documentsRouteId) {
      refetchRouteDocuments?.().finally(() => setIsLoadingDocuments(false));
    } else {
      setIsLoadingDocuments(false);
    }
  };

  const getProfitColor = (profit: number) => {
    return profit > 0 ? '#52c41a' : '#ff4d4f';
  };

  const getDocsStatusColor = (status: string) => {
    switch (status) {
      case 'SIGNED': return 'green';
      case 'NOT_SIGNED': return 'red';
      default: return 'orange';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'RECEIVED': return 'green';
      case 'NOT_RECEIVED': return 'red';
      default: return 'orange';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'PAID': return 'Оплачен';
      case 'PARTIAL': return 'Частично';
      case 'PENDING': return 'Ожидает';
      case 'OVERDUE': return 'Просрочен';
      default: return status;
    }
  };

  // Хелпер для расчёта счётчика отсрочки
  const calculateDelayStatus = (startDate: string | null, delayDays: number | null) => {
    if (!startDate || !delayDays) return { text: 'Не запущен', color: 'default', daysLeft: null };
    const start = new Date(startDate);
    const due = new Date(start.getTime() + delayDays * 86400000);
    const now = Date.now();
    const daysLeft = Math.ceil((due.getTime() - now) / 86400000);
    const overdue = daysLeft < 0;
    return {
      text: overdue ? `Просрочен на ${Math.abs(daysLeft)} дн.` : `${daysLeft} дн.`,
      color: overdue ? 'red' : daysLeft <= 3 ? 'orange' : 'blue',
      daysLeft,
      dueDate: due
    };
  };

  // Колонки для своих заявок (с счётчиками оплаты)
  const companyApplicationsColumns: ColumnsType<any> = [
    {
      title: 'Заявка',
      dataIndex: 'internalNumber',
      key: 'internalNumber',
      width: 100,
      render: (_: string, record: { id?: number; internalNumber?: string | null; applicationDate?: string | null }) => (
        <ApplicationCell application={record} />
      ),
    },
    {
      title: 'Клиент',
      dataIndex: ['client', 'name'],
      key: 'clientName',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Логист',
      dataIndex: ['responsibleLogist'],
      key: 'responsibleLogist',
      width: 150,
      ellipsis: true,
      render: (logist: any) => {
        if (!logist) return <Text type="secondary">Не назначен</Text>;
        return <Text>{formatResponsibleUserLabel(logist)}</Text>;
      }
    },
    {
      title: 'Основание',
      key: 'applicationBasis',
      width: 150,
      render: (_: any, record: any) => {
        const basis = record.applicationBasis;
        if (!basis) {
          return <Text type="secondary">—</Text>;
        }
        const basisLabels: Record<string, string> = {
          'PARTNER_CONTRACT': 'Договор с партнёром',
          'CLIENT_CONTRACT': 'По договору',
          'CLIENT_CONTRACT_REQUEST': 'Договор-заявка'
        };
        const label = basisLabels[basis] || basis;
        const basisNumber = record.basisNumber;
        return (
          <div style={{ fontSize: 12 }}>
            <div>{label}</div>
            {basisNumber && (
              <div style={{ color: '#999', marginTop: 2 }}>№ {basisNumber}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Ставка',
      key: 'rate',
      width: 120,
      render: (_: any, record: any) => (
        <Text strong>
          <MoneyCell
            value={record.totalCostWithVAT || record.financialSummary?.clientRate || 0}
            type="income"
          />
        </Text>
      ),
    },
    {
      title: 'Затраты',
      dataIndex: ['financialSummary', 'totalRoutesCost'],
      key: 'totalRoutesCost',
      width: 120,
      render: (value: number) => <MoneyCell value={value} type="expense" />,
    },
    {
      title: 'Прибыль',
      key: 'profit',
      width: 120,
      render: (_: any, record: any) => {
        const rate = record.totalCostWithVAT || record.financialSummary?.clientRate || 0;
        const costs = record.financialSummary?.totalRoutesCost || 0;
        const profit = rate - costs;
        return (
          <Text strong>
            <MoneyCell
              value={profit}
              type={profit > 0 ? 'income' : profit < 0 ? 'expense' : 'neutral'}
            />
          </Text>
        );
      },
    },
    {
      title: 'Предоплата',
      key: 'prepayment',
      width: 220,
      render: (_: any, record: any) => {
        const rate = record.totalCostWithVAT || record.financialSummary?.clientRate || 0;
        const percent = record.prepaymentPercent;
        // Если предоплата не задана - показываем подсказку
        if (percent === null || percent === undefined) {
          return <Text type="secondary" style={{ fontSize: 10 }}>Не настроено</Text>;
        }
        // Если предоплата 0% - не показываем
        if (percent === 0) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        const expectedAmount = rate * percent / 100;
        const receivedAmount = Number(record.prepaymentReceivedAmount) || 0;
        const status = calculateDelayStatus(record.prepaymentStartDate, record.prepaymentDelayDays);
        const isPaid = record.prepaymentStatus === 'PAID';
        const isPartial = record.prepaymentStatus === 'PARTIAL';
        const isStarted = !!record.prepaymentStartDate;
        return (
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: 11 }}>
              К оплате: <MoneyCell value={expectedAmount} type="neutral" />
            </Text>
            {receivedAmount > 0 && (
              <Text style={{ fontSize: 11 }}>
                Получено: <MoneyCell value={receivedAmount} type="income" />
              </Text>
            )}
            {isPaid ? (
              <Tag color="green" style={{ margin: 0 }}>✓ Оплачено</Tag>
            ) : isPartial ? (
              <Tag color="orange" style={{ margin: 0 }}>Частично</Tag>
            ) : isStarted ? (
              <Space size={4}>
                <Tag color={status.color} style={{ margin: 0 }}>{status.text}</Tag>
                <Tooltip title="Отметить оплаченной">
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleMarkPaid(record.id, 'prepayment')}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Button>
                </Tooltip>
              </Space>
            ) : (
              <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }} onClick={() => openCounterModal(record.id, 'prepayment')}>
                ▶ Запустить
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Доплата',
      key: 'mainPayment',
      width: 220,
      render: (_: any, record: any) => {
        const rate = record.totalCostWithVAT || record.financialSummary?.clientRate || 0;
        const prepayPercent = record.prepaymentPercent;
        // Если условия оплаты не настроены - показываем подсказку
        if (prepayPercent === null || prepayPercent === undefined) {
          return <Text type="secondary" style={{ fontSize: 10 }}>Не настроено</Text>;
        }
        const percent = record.mainPaymentPercent ?? (100 - prepayPercent);
        // Если доплата 0% (100% предоплата) - не показываем
        if (percent === 0) {
          return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        }
        const expectedAmount = rate * percent / 100;
        const receivedAmount = Number(record.mainPaymentReceivedAmount) || 0;
        const status = calculateDelayStatus(record.mainPaymentStartDate, record.mainPaymentDelayDays);
        const isPaid = record.mainPaymentStatus === 'PAID';
        const isPartial = record.mainPaymentStatus === 'PARTIAL';
        const isStarted = !!record.mainPaymentStartDate;
        return (
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: 11 }}>
              К оплате: <MoneyCell value={expectedAmount} type="neutral" />
            </Text>
            {receivedAmount > 0 && (
              <Text style={{ fontSize: 11 }}>
                Получено: <MoneyCell value={receivedAmount} type="income" />
              </Text>
            )}
            {isPaid ? (
              <Tag color="green" style={{ margin: 0 }}>✓ Оплачено</Tag>
            ) : isPartial ? (
              <Tag color="orange" style={{ margin: 0 }}>Частично</Tag>
            ) : isStarted ? (
              <Space size={4}>
                <Tag color={status.color} style={{ margin: 0 }}>{status.text}</Tag>
                <Tooltip title="Отметить оплаченной">
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleMarkPaid(record.id, 'main')}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Button>
                </Tooltip>
              </Space>
            ) : (
              <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }} onClick={() => openCounterModal(record.id, 'main')}>
                ▶ Запустить
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => <StatusCell status={status} />,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => {
            setApplicationIdForRoutes(record.id);
            setApplicationInternalNumberForRoutes(record.internalNumber);
            setIsRoutesDrawerOpen(true);
          }}>Маршруты</Button>
          <Button type="link" size="small" onClick={() => handleUpdateApplication(record)}>Изменить</Button>
        </Space>
      ),
    },
  ];

  // Колонки для партнёрских заявок
  const partnerApplicationsColumns: ColumnsType<any> = [
    {
      title: 'Номер заявки',
      dataIndex: 'internalNumber',
      key: 'internalNumber',
      render: (_: string, record: { id?: number; internalNumber?: string | null; applicationDate?: string | null }) => (
        <ApplicationCell application={record} />
      ),
    },
    {
      title: 'Внешний номер',
      dataIndex: 'externalNumber',
      key: 'externalNumber',
      render: (_: string, record: { externalNumber?: string; basisDate?: string | null; applicationDate?: string | null }) => (
        <ExternalApplicationCell application={record} />
      ),
    },
    {
      title: 'Клиент',
      dataIndex: ['client', 'name'],
      key: 'clientName',
    },
    {
      title: 'Логист',
      dataIndex: ['responsibleLogist'],
      key: 'responsibleLogist',
      width: 150,
      ellipsis: true,
      render: (logist: any) => {
        if (!logist) return <Text type="secondary">Не назначен</Text>;
        return <Text>{formatResponsibleUserLabel(logist)}</Text>;
      }
    },
    {
      title: 'Основание',
      key: 'applicationBasis',
      width: 150,
      render: (_: any, record: any) => {
        const basis = record.applicationBasis;
        if (!basis) {
          return <Text type="secondary">—</Text>;
        }
        const basisLabels: Record<string, string> = {
          'PARTNER_CONTRACT': 'Договор с партнёром',
          'CLIENT_CONTRACT': 'По договору',
          'CLIENT_CONTRACT_REQUEST': 'Договор-заявка'
        };
        const label = basisLabels[basis] || basis;
        const basisNumber = record.basisNumber;
        return (
          <div style={{ fontSize: 12 }}>
            <div>{label}</div>
            {basisNumber && (
              <div style={{ color: '#999', marginTop: 2 }}>№ {basisNumber}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <StatusCell status={status} />,
    },
    {
      title: 'Ставка клиента',
      dataIndex: ['financialSummary', 'clientRate'],
      key: 'clientRate',
      render: (value: number) => (
        <Text strong>
          <MoneyCell value={value} type="income" />
        </Text>
      ),
    },
    {
      title: 'Затраты на маршруты',
      dataIndex: ['financialSummary', 'totalRoutesCost'],
      key: 'totalRoutesCost',
      render: (value: number) => <MoneyCell value={value} type="expense" />,
    },
    {
      title: 'К получению от партнера',
      dataIndex: ['financialSummary', 'partnerPaymentAmount'],
      key: 'partnerPaymentAmount',
      render: (value: number) => <MoneyCell value={value} type="income" />,
    },
    {
      title: 'Получено от партнёра',
      dataIndex: ['financialSummary', 'partnerReceived'],
      key: 'partnerReceived',
      render: (value: number) => <MoneyCell value={value} type="income" />,
    },
    {
      title: 'Прибыль (ожид.)',
      dataIndex: ['financialSummary', 'expectedProfit'],
      key: 'expectedProfit',
      render: (value: number) => (
        <Text strong>
          <MoneyCell
            value={value}
            type={value > 0 ? 'income' : value < 0 ? 'expense' : 'neutral'}
          />
        </Text>
      ),
    },
    {
      title: 'Прибыль (реал.)',
      dataIndex: ['financialSummary', 'realizedProfit'],
      key: 'realizedProfit',
      render: (value: number) => (
        <Text strong>
          <MoneyCell
            value={value}
            type={value > 0 ? 'income' : value < 0 ? 'expense' : 'neutral'}
          />
        </Text>
      ),
    },
    {
      title: 'Первичные документы',
      dataIndex: 'primaryDocsStatus',
      key: 'primaryDocsStatus',
      render: (status: string) => (
        <Tag color={getDocsStatusColor(status)}>
          {status === 'SIGNED' ? 'Подписаны' : 'Не подписаны'}
          </Tag>
      ),
    },
    {
      title: 'Платеж от партнера',
      dataIndex: 'partnerPaymentStatus',
      key: 'partnerPaymentStatus',
      render: (status: string) => (
        <Tag color={getPaymentStatusColor(status)}>
          {status === 'RECEIVED' ? 'Получен' : 'Не получен'}
        </Tag>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleUpdateApplication(record)} size="small">
            Изменить
          </Button>
          <Button type="link" icon={<EyeOutlined />} onClick={() => {
            setApplicationIdForRoutes(record.id);
            setApplicationInternalNumberForRoutes(record.internalNumber);
            setIsRoutesDrawerOpen(true);
          }} size="small">
            Просмотр
          </Button>
          <Button type="link" onClick={() => {
            setUploadModalApplication(record);
            setUploadModalVisible(true);
          }} size="small">
            Загрузить первичные
          </Button>
        </Space>
      ),
    },
  ];

  // Колонки для таблицы маршрутов
  const normalizeRouteStatus = (rawStatus?: string) => {
    if (!rawStatus) return 'active';
    const s = String(rawStatus).toLowerCase();
    if (s.includes('complete')) return 'completed';
    if (s.includes('cancel')) return 'cancelled';
    if (s.includes('active') || s.includes('create') || s.includes('in_transit') || s.includes('in-transit')) return 'active';
    return s as 'active' | 'completed' | 'cancelled';
  };

  const routesColumns: ColumnsType<any> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Text strong>#{id}</Text>
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: (a: any, b: any) => normalizeRouteStatus(a.status).localeCompare(normalizeRouteStatus(b.status)),
      render: (status: string) => {
        const s = normalizeRouteStatus(status);
        const color = s === 'completed' ? 'green' : s === 'cancelled' ? 'red' : 'blue';
        const label = s === 'completed' ? 'Завершен' : s === 'cancelled' ? 'Отменен' : 'Активный';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Заявки',
      key: 'application',
      width: 110,
      render: (_, record) => {
        const app = record.application || record.applications?.[0]?.application;
        return (
          <div>
            <div style={{ color: 'var(--text)', fontWeight: 500 }}>
              {app?.internalNumber || 'Н/Д'}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Тип',
      dataIndex: 'transportType',
      key: 'transportType',
      width: 90,
      render: (type: string, record: any) => {
        // Try several fallbacks because backend field names may vary
        const t = type || record.transportType || record.route?.transportType || record.transport_type || record.type;
        const normalized = String(t || '').toUpperCase();

        const icons: Record<string, React.ReactNode> = {
          'AUTO': <CarOutlined />,
          'RAILWAY': <RocketOutlined />,
          'AVIATION': <SendOutlined />
        };

        const labels: Record<string, string> = {
          'AUTO': 'Авто',
          'RAILWAY': 'ЖД',
          'AVIATION': 'Авиа'
        };

        const colors: Record<string, string> = {
          'AUTO': '#1890ff',
          'RAILWAY': '#52c41a',
          'AVIATION': '#fa8c16'
        };

        if (!normalized) return <Text type="secondary">-</Text>;

        return (
          <Space size="small">
            <span style={{ color: colors[normalized] || undefined, fontSize: 18, display: 'inline-flex', alignItems: 'center' }}>
              {icons[normalized] || null}
            </span>
            <span style={{ fontSize: '12px' }}>{labels[normalized] || t}</span>
          </Space>
        );
      },
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 560,
      ellipsis: true,
      render: (_, record) => {
        const from = record.loadingAddress || 'Не указано';
        const to = record.unloadingAddress || 'Не указано';
        
        // Извлекаем только название населенного пункта (до первой запятой)
        const getCity = (address: string) => {
          if (address === 'Не указано') return 'Не указано';
          const city = address.split(',')[0].trim();
          return city;
        };
        
        const fromCity = getCity(from);
        const toCity = getCity(to);
        
        return (
          <div style={{ color: 'var(--text)' }}>
            <Space size={8} style={{ marginBottom: 4 }}>
              {record.isCompanyRoute ? (
                <Tag color="green">Свой</Tag>
              ) : (
                <Tag color="blue">Партнёрский</Tag>
              )}
            </Space>
            {fromCity === 'Не указано' && toCity === 'Не указано' ? (
              <Text style={{ color: 'var(--text-secondary)' }}>Не указано</Text>
            ) : (
              <div style={{ whiteSpace: 'nowrap' }}>
                {fromCity} → {toCity}
              </div>
            )}
            <div style={{ marginTop: 2, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              {(record.shipper?.name || '—')} → {(record.consignee?.name || '—')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Логист',
      key: 'responsibleLogist',
      width: 140,
      ellipsis: true,
      render: (_: any, record: any) => {
        const name = formatResponsibleUserLabel(record.responsibleLogist);
        return <Text>{name}</Text>;
      }
    },
    {
      title: 'Договор-заявка',
      key: 'carrierContractRequest',
      width: 200,
      render: (_: any, record: any) => {
        const contractNumber = (record as any).carrierContractRequestNumber;
        const contractDate = (record as any).carrierContractRequestDate;
        if (!contractNumber && !contractDate) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div style={{ fontSize: 12 }}>
            {contractNumber && <div style={{ fontWeight: 500 }}>{contractNumber}</div>}
            {contractDate && (
              <div style={{ color: 'var(--text-secondary)' }}>
                {dayjs(contractDate).format('DD.MM.YYYY')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Перевозчик / Водитель',
      key: 'carrier',
      width: 260,
      ellipsis: true,
      render: (_: any, record: any) => {
        const carrierName = record.carrier?.name || '—';
        const driverName = record.driver?.fullName || record.driver?.name || record.driver?.phone || null;
        return (
          <div>
            <div>{carrierName}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              {driverName ? `Водитель: ${driverName}` : 'Водитель: —'}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Дата погрузки',
      key: 'loadingDates',
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>пл: {record.plannedLoadingDate ? dayjs(record.plannedLoadingDate).format('DD.MM.YY') : '—'}</div>
          <div>ф: {record.actualLoadingDate ? dayjs(record.actualLoadingDate).format('DD.MM.YY') : '—'}</div>
        </div>
      )
    },
    {
      title: 'Дата выгрузки',
      key: 'unloadingDates',
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>пл: {record.plannedUnloadingDate ? dayjs(record.plannedUnloadingDate).format('DD.MM.YY') : '—'}</div>
          <div>ф: {record.actualUnloadingDate ? dayjs(record.actualUnloadingDate).format('DD.MM.YY') : '—'}</div>
        </div>
      )
    },

    {
      title: 'Клиент',
      key: 'client',
      width: 300,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ color: 'var(--text)' }}>
          {record.application?.client?.name || 'Н/Д'}
        </div>
      ),
    },
    {
      title: 'Ставка',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      sorter: (a: any, b: any) => (a.totalAmount || 0) - (b.totalAmount || 0),
      render: (amount: number) => (
        <Text strong>
          <MoneyCell value={amount} type="neutral" />
        </Text>
      ),
    },
    {
      title: 'Доход',
      key: 'income',
      width: 140,
      render: (_, record) => {
        // Доход отображаем только для собственных маршрутов (COMPANY)
        if (record.isCompanyRoute) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {record.income ? (
                  <Text strong>
                    <MoneyCell value={record.income} type="income" />
                  </Text>
                ) : (
                  <Text type="warning" style={{ whiteSpace: 'nowrap' }}>Не указано</Text>
                )}
              </div>
              <Tooltip title="Изменить">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditRouteIncome(record);
                  }}
                  style={{ padding: 0, height: 'auto', fontSize: 14 }}
                />
              </Tooltip>
            </div>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Затраты',
      key: 'costs',
      width: 120,
      render: (_, record) => {
        // Для маршрутов, оплачиваемых партнёром (executor !== 'COMPANY'), затраты не считаем в нашей прибыли
        // Но отображать сумму всё равно имеет смысл — пометим её как secondary
        const amt = Number(record.totalAmount || 0);
        return record.isCompanyRoute ? (
          <MoneyCell value={amt} type="expense" />
        ) : (
          <Text type="secondary">
            <MoneyCell value={amt} type="neutral" />
          </Text>
        );
      },
    },
    {
      title: 'Прибыль',
      key: 'profit',
      width: 120,
      render: (_, record) => {
        // Если маршрут оплачивается партнёром или это ЖД-маршрут с executor === 'PARTNER',
        // считаем, что оплата и расчёт прибыли для нашей компании не требуется — показываем '-'
        const executor = record.executor || record.payer || undefined;
        const isPartnerPaid = executor && String(executor).toUpperCase() !== 'COMPANY';

        if (isPartnerPaid) {
          return <Text type="secondary">-</Text>;
        }

        // Иначе считаем прибыль как income - cost
        const income = record.income != null ? Number(record.income) : null;
        const cost = record.totalAmount != null ? Number(record.totalAmount) : null;
        if (income !== null || cost !== null) {
          const profit = (income || 0) - (cost || 0);
          return (
            <Text strong>
              <MoneyCell
                value={profit}
                type={profit > 0 ? 'income' : profit < 0 ? 'expense' : 'neutral'}
              />
            </Text>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Остаток долга',
      key: 'debtBreakdown',
      width: 190,
      render: (_: any, record: any) => {
        const obligations = record.obligations || [];
        if (!obligations.length) return <Text type="secondary">—</Text>;
        const prepayment = obligations.find((o: any) => o.stage === 'PREPAYMENT');
        const finalPayment = obligations.find((o: any) => o.stage === 'FINAL');
        const preDebt = prepayment ? Number(prepayment.remainingAmount || 0) : 0;
        const finalDebt = finalPayment ? Number(finalPayment.remainingAmount || 0) : 0;
        return (
          <div style={{ minWidth: 160 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Предоплата</span>
              <Tag color={preDebt > 0 ? 'volcano' : 'green'} style={{ fontSize: '10px' }}>
                {preDebt > 0 ? 'Долг' : 'Закрыт'}
              </Tag>
            </div>
            <div style={{ fontSize: '12px' }}>
              <MoneyCell value={preDebt} type={preDebt > 0 ? 'expense' : 'income'} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ color: 'var(--text-secondary)' }}>Доплата</span>
              <Tag color={finalDebt > 0 ? 'volcano' : 'green'} style={{ fontSize: '10px' }}>
                {finalDebt > 0 ? 'Долг' : 'Закрыт'}
              </Tag>
            </div>
            <div style={{ fontSize: '12px' }}>
              <MoneyCell value={finalDebt} type={finalDebt > 0 ? 'expense' : 'income'} />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Предоплата',
      key: 'prepayment',
      width: 190,
      render: (_, record) => {
        const obligations = record.obligations || [];
        if (!obligations.length) return <Text type="secondary">—</Text>;
        const prepayment = obligations.find((o: any) => o.stage === 'PREPAYMENT');
        if (!prepayment) return <Text type="secondary">—</Text>;
        const remainingAmount = Number(prepayment.remainingAmount || 0);
        const status = prepayment.status;
        const color = status === 'PAID' ? 'green' : status === 'PARTIALLY_PAID' || status === 'PARTIAL' ? 'orange' : status === 'OVERDUE' ? 'red' : 'default';
        const label = status === 'PAID' ? 'Внесена' : status === 'PARTIALLY_PAID' || status === 'PARTIAL' ? 'Частично' : status === 'OVERDUE' ? 'Просрочено' : 'Не внесена';
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px' }}>
                <MoneyCell value={remainingAmount} type="neutral" />
              </span>
            </div>
            <Tag color={color} style={{ marginTop: 4 }}>{label}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Отсрочка предоплаты',
      key: 'prepaymentDelay',
      width: 160,
      render: (_, record) => {
        const obligations = record.obligations || [];
        if (!obligations.length) return <Text type="secondary">—</Text>;
        const prepayment = obligations.find((o: any) => o.stage === 'PREPAYMENT');
        if (!prepayment) return <Text type="secondary">—</Text>;
        const status = prepayment.status;
        if (status === 'PAID') return <StatusCell status="PAID" />;
        if (status === 'OVERDUE') return <StatusCell status="OVERDUE" />;
        if (status === 'PARTIALLY_PAID' || status === 'PARTIAL') return <StatusCell status="PARTIAL" />;
        return <StatusCell status="PENDING" />;
      },
    },
    {
      title: 'Доплата',
      key: 'finalPayment',
      width: 190,
      render: (_, record) => {
        const obligations = record.obligations || [];
        if (!obligations.length) return <Text type="secondary">—</Text>;
        const finalPayment = obligations.find((o: any) => o.stage === 'FINAL');
        if (!finalPayment) return <Text type="success">Не требуется</Text>;
        const remainingAmount = Number(finalPayment.remainingAmount || 0);
        const status = finalPayment.status;
        const st =
          status === 'PAID'
            ? 'PAID'
            : status === 'PARTIALLY_PAID' || status === 'PARTIAL'
              ? 'PARTIAL'
              : status === 'OVERDUE'
                ? 'OVERDUE'
                : 'NOT_PAID';
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px' }}>
                <MoneyCell value={remainingAmount} type="neutral" />
              </span>
            </div>
            <StatusCell status={st} style={{ marginTop: 4 }} />
          </div>
        );
      },
    },
    {
      title: 'Отсрочка доплаты',
      key: 'mainPaymentDelay',
      width: 160,
      render: (_, record) => {
        const obligations = record.obligations || [];
        if (!obligations.length) return <Text type="secondary">—</Text>;
        const finalPayment = obligations.find((o: any) => o.stage === 'FINAL');
        if (!finalPayment) return <Text type="success">Не требуется</Text>;
        const status = finalPayment.status;
        if (status === 'PAID') return <StatusCell status="PAID" />;
        if (status === 'OVERDUE') return <StatusCell status="OVERDUE" />;
        if (status === 'PARTIALLY_PAID' || status === 'PARTIAL') return <StatusCell status="PARTIAL" />;
        return <StatusCell status="PENDING" />;
      },
    },
    {
      title: 'Документы',
      key: 'documents',
      width: 240,
      fixed: 'right',
      filters: [
        { text: 'Есть счет и фото', value: 'has_loading' },
        { text: 'Нет счета и фото', value: 'no_loading' },
        { text: 'Есть ТН', value: 'has_transport' },
        { text: 'Нет ТН', value: 'no_transport' },
      ],
      onFilter: (value, record: any) => {
        const v = String(value as any);
        if (v === 'has_loading') return (record.loadingDocuments?.length || 0) > 0;
        if (v === 'no_loading') return (record.loadingDocuments?.length || 0) === 0;
        if (v === 'has_transport') return (record.transportDocuments?.length || 0) > 0;
        if (v === 'no_transport') return (record.transportDocuments?.length || 0) === 0;
        return true;
      },
      render: (_, record) => {
        const ld = Array.isArray(record.loadingDocuments) ? record.loadingDocuments.length : 0;
        const td = Array.isArray(record.transportDocuments) ? record.transportDocuments.length : 0;
        const totalDocs = ld + td;
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
              {record.loadingDocuments?.length > 0 ? (
                <Tag color="green">Есть счет и фото</Tag>
              ) : (
                <>
                  <Tag color="red">Нет счета и фото</Tag>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<UploadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRouteForUpload(record);
                      setIsUploadLoadingDocsModalVisible(true);
                    }}
                    style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                  >
                    Загрузить
                  </Button>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
              {record.transportDocuments?.length > 0 ? (
                <Tag color="green">Есть ТН</Tag>
              ) : (
                <>
                  <Tag color="red">Нет ТН</Tag>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<UploadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRouteForUpload(record);
                      setIsUploadTransportDocsModalVisible(true);
                    }}
                    style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                  >
                    Загрузить
                  </Button>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Button 
                type="link" 
                size="small" 
                icon={<FileTextOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('documents');
                  setDocumentsRouteId(record.id);
                }}
                style={{ padding: 0, height: 'auto', fontSize: '14px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <FileTextOutlined style={{ fontSize: 16 }} />
                <span>{totalDocs}</span>
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Оплата',
      key: 'aggregatedStatus',
      width: 120,
      fixed: 'right',
      render: (_: any, record: any) => {
        const overallStatus = record.aggregatedStatus?.overallStatus;
        if (!overallStatus && !(record.obligations || []).length) return <Text type="secondary">—</Text>;
        return <StatusCell status={overallStatus || 'OPEN'} />;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const hasExternalDriver = !!(record.driver && record.driver.isCompanyDriver === false);
        const hasCarrierAssigned = !!record.carrier;
        const canCreatePayment = !record.isCompanyRoute || hasExternalDriver || hasCarrierAssigned;
        return (
          <Tooltip title={canCreatePayment ? undefined : 'Маршрут внутренний — платеж недоступен, если только нет внешнего водителя или назначенного перевозчика'}>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              disabled={!canCreatePayment}
              onClick={(e) => {
                e.stopPropagation();
                handleCreatePaymentForRoute(record);
              }}
            >
              {canCreatePayment ? 'Платеж' : 'Недоступно'}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  // Колонки для вкладки "Маршруты" в разделе "Логисты" (макет как на скриншоте)
  const logistRoutesColumns: ColumnsType<any> = [
    {
      title: '№ маршрута',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      render: (id: number) => <Text strong>#{id}</Text>,
      fixed: 'left',
    },
    {
      title: '№ заявки',
      key: 'applicationNumber',
      width: 180,
      fixed: 'left',
      render: (_: any, record: any) => {
        const app = record.application || record.applications?.[0]?.application;
        if (!app) return <Text type="secondary">—</Text>;
        const { line1, line2 } = workspaceApplicationNumberLines(app.internalNumber, app.applicationDate);
        return <WorkspaceTwoLineTableCell line1={line1} line2={line2} />;
      },
    },
    {
      title: '№ маршрута + дата',
      key: 'routeNumber',
      width: 170,
      render: (_: any, record: any) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div>Маршрут #{record.id}</div>
          {(record.plannedLoadingDate || record.createdAt) && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              от {dayjs(record.plannedLoadingDate || record.createdAt).format('DD.MM.YY')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Тип маршрута',
      key: 'routeType',
      width: 120,
      render: (_: any, record: any) =>
        record.isCompanyRoute ? <Tag color="green">Свой</Tag> : <Tag color="blue">Партнёрский</Tag>,
    },
    {
      title: 'Тип',
      dataIndex: 'transportType',
      key: 'transportType',
      width: 90,
      render: (type: string, record: any) => {
        const t = type || record.transportType || record.route?.transportType || record.transport_type || record.type;
        const normalized = String(t || '').toUpperCase();

        const icons: Record<string, React.ReactNode> = {
          'AUTO': <CarOutlined />,
          'RAILWAY': <RocketOutlined />,
          'AVIATION': <SendOutlined />
        };

        const labels: Record<string, string> = {
          'AUTO': 'Авто',
          'RAILWAY': 'ЖД',
          'AVIATION': 'Авиа'
        };

        const colors: Record<string, string> = {
          'AUTO': '#1890ff',
          'RAILWAY': '#52c41a',
          'AVIATION': '#fa8c16'
        };

        if (!normalized) return <Text type="secondary">-</Text>;

        return (
          <Space size="small">
            <span style={{ color: colors[normalized] || undefined, fontSize: 18, display: 'inline-flex', alignItems: 'center' }}>
              {icons[normalized] || null}
            </span>
            <span style={{ fontSize: '12px' }}>{labels[normalized] || t}</span>
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => {
        const s = normalizeRouteStatus(status);
        const color = s === 'completed' ? 'green' : s === 'cancelled' ? 'red' : 'blue';
        const label = s === 'completed' ? 'Завершен' : s === 'cancelled' ? 'Отменен' : 'Активный';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 420,
      ellipsis: true,
      render: (_: any, record: any) => {
        const from = record.loadingAddress || 'Не указано';
        const to = record.unloadingAddress || 'Не указано';

        const getCity = (address: string) => {
          if (address === 'Не указано') return 'Не указано';
          const city = address.split(',')[0].trim();
          return city;
        };

        const fromCity = getCity(from);
        const toCity = getCity(to);

        return (
          <div style={{ color: 'var(--text)' }}>
            {fromCity === 'Не указано' && toCity === 'Не указано' ? (
              <Text style={{ color: 'var(--text-secondary)' }}>Не указано</Text>
            ) : (
              <div style={{ whiteSpace: 'nowrap' }}>
                {fromCity} → {toCity}
              </div>
            )}
            <div style={{ marginTop: 2, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              {(record.shipper?.name || '—')} → {(record.consignee?.name || '—')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Клиент',
      key: 'client',
      width: 220,
      ellipsis: true,
      render: (_: any, record: any) => (
        <Text>{record.application?.client?.name || record.applications?.[0]?.application?.client?.name || 'Н/Д'}</Text>
      ),
    },
    {
      title: 'Дата погрузки',
      key: 'loadingDates',
      width: 140,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>пл: {record.plannedLoadingDate ? dayjs(record.plannedLoadingDate).format('DD.MM.YY') : '—'}</div>
          <div>ф: {record.actualLoadingDate ? dayjs(record.actualLoadingDate).format('DD.MM.YY') : '—'}</div>
        </div>
      ),
    },
    {
      title: 'Дата выгрузки',
      key: 'unloadingDates',
      width: 140,
      render: (_: any, record: any) => (
        <div style={{ fontSize: 12 }}>
          <div>пл: {record.plannedUnloadingDate ? dayjs(record.plannedUnloadingDate).format('DD.MM.YY') : '—'}</div>
          <div>ф: {record.actualUnloadingDate ? dayjs(record.actualUnloadingDate).format('DD.MM.YY') : '—'}</div>
        </div>
      ),
    },
    {
      title: 'Перевозчик',
      key: 'carrier',
      width: 180,
      ellipsis: true,
      render: (_: any, record: any) => <Text>{record.carrier?.name || '—'}</Text>,
    },
    {
      title: 'Водитель',
      key: 'driver',
      width: 180,
      ellipsis: true,
      render: (_: any, record: any) => {
        const driverName = record.driver?.fullName || record.driver?.name || record.driver?.phone;
        return <Text>{driverName || '—'}</Text>;
      },
    },
    {
      title: 'Ставка',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text strong>
          <MoneyCell value={amount} type="neutral" />
        </Text>
      ),
    },
    {
      title: 'Статус оплаты',
      key: 'aggregatedStatus',
      width: 150,
      render: (_: any, record: any) => {
        const overallStatus = record.aggregatedStatus?.overallStatus;
        if (!overallStatus) return <Text type="secondary">—</Text>;
        return <StatusCell status={overallStatus} />;
      },
    },
    {
      title: 'Документы погрузка',
      key: 'loadingDocs',
      width: 160,
      render: (_: any, record: any) => {
        const count = Array.isArray(record.loadingDocuments) ? record.loadingDocuments.length : 0;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {count > 0 ? <Tag color="green">Есть ({count})</Tag> : <Tag color="red">Нет</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Документы выгрузка',
      key: 'unloadingDocs',
      width: 160,
      render: (_: any, record: any) => {
        const count = Array.isArray(record.transportDocuments) ? record.transportDocuments.length : 0;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {count > 0 ? <Tag color="green">Есть ({count})</Tag> : <Tag color="red">Нет</Tag>}
          </span>
        );
      },
    },
  ];

  // Колонки для таблицы платежей
  const paymentsColumns: ColumnsType<Payment> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Тип',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (_: string, record: Payment) => (
        <Tag color={getRegistryPaymentTypeTagColor(record)}>
          {getRegistryPaymentTypeLabel(record)}
        </Tag>
      ),
    },
    {
      title: 'Маршрут',
      key: 'route',
      render: (_, record) => (
        <div>
          {record.route ? (
            <div>
              <div>№{record.route.id}</div>
              <div style={{ color: '#666', fontSize: '12px' }}>
                {record.route.transportType === 'AUTO' ? 'Авто' : 
                 record.route.transportType === 'RAILWAY' ? 'ЖД' : 
                 record.route.transportType === 'AVIATION' ? 'Авиа' : record.route.transportType}
              </div>
            </div>
          ) : (
            <Text type="secondary">Н/Д</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Перевозчик',
      key: 'carrier',
      render: (_, record) => (
        <div>
          {record.route?.carrier?.name || 'Н/Д'}
        </div>
      ),
    },
    {
      title: 'Тип маршрута',
      key: 'routeType',
      render: (_, record) => (
        <div>
          {record.route?.isCompanyRoute ? (
            <Tag color="purple">Свой</Tag>
          ) : (
            <Text type="secondary">Внешний</Text>
          )}
        </div>
      ),
    },
    {
      title: 'К оплате',
      dataIndex: 'amountToPay',
      key: 'amountToPay',
      render: (amount: number) => (
        <Text strong>
          <MoneyCell value={amount} type="neutral" />
        </Text>
      ),
    },
    {
      title: 'Оплачено',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      render: (amount: number) =>
        amount > 0 ? (
          <MoneyCell value={amount} type="income" />
        ) : (
          <Text type="secondary">
            <MoneyCell value={amount} type="neutral" />
          </Text>
        ),
    },
    {
      title: 'Остаток',
      key: 'remaining',
      render: (_, record) => {
        const remaining = record.amountToPay - record.amountPaid;
        return (
          <MoneyCell
            value={remaining}
            type={remaining > 0 ? 'expense' : remaining < 0 ? 'income' : 'neutral'}
          />
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getPaymentStatusColor(status)}>
          {getPaymentStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Дата платежа',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => (
        <div>
          {date ? dayjs(date).format('DD.MM.YYYY') : '-'}
        </div>
      ),
    },
    {
      title: 'Дней до оплаты',
      dataIndex: 'daysToPayment',
      key: 'daysToPayment',
      render: (days: number) => (
        <div>
          {days !== null ? (
            <Text style={{ color: days < 0 ? '#ff4d4f' : days <= 3 ? '#faad14' : '#666' }}>
              {days} дн.
        </Text>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleUpdatePayment(record)}
          >
            Изменить
          </Button>
          <Button
            type="link"
            size="small"
            danger
            onClick={() => handleDeletePayment(record)}
          >
            Удалить
          </Button>
        </Space>
      ),
    },
  ];

  // Колонки для таблицы документов погрузки
  const loadingDocumentsColumns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Тип',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type: string) => (
        <Tag color={type === 'photo' ? 'green' : 'blue'}>{type === 'photo' ? 'Фото' : 'Документ'}</Tag>
      )
    },
    {
      title: 'Название',
      key: 'fileName',
      render: (_: unknown, record: any) => resolveDocumentFilename(record),
    },
    {
      title: 'Дата загрузки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewFinanceDocument(record)}
          >
            Просмотр
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadFinanceDocument(record)}
          >
            Скачать
          </Button>
        </Space>
      ),
    },
  ];

  // Колонки для таблицы транспортных документов
  const transportDocumentsColumns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Название',
      key: 'fileName',
      render: (_: unknown, record: any) => resolveDocumentFilename(record),
    },
    {
      title: 'Тип',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (type: string) => (
        <Tag color={type === 'photo' ? 'green' : 'blue'}>{type === 'photo' ? 'Фото' : 'Документ'}</Tag>
      )
    },
    {
      title: 'Дата получения',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewFinanceDocument(record)}
          >
            Просмотр
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadFinanceDocument(record)}
          >
            Скачать
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoadingStats) {
    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Spin size="large" />
      </div>
    );
  }

  const financialStats = statistics || {
    totalApplications: 0,
    completedApplications: 0,
    signedDocsApplications: 0,
    receivedPaymentsApplications: 0,
    financialSummary: {
      totalClientRate: 0,
      totalRoutesCost: 0,
      totalPartnerRoutesCost: 0,
      totalOurRoutesCost: 0,
      totalPartnerPaymentAmount: 0,
      totalProfit: 0
    }
  };

  // Просмотр документа (универсальный для финансового раздела)
  const handleViewFinanceDocument = async (document: any) => {
    try {
      const displayName = resolveDocumentFilename(document);
      const { url, revoke } = await fetchDocumentObjectUrl(document.id, documentPreviewEndpoint(document.fileType));
      Modal.info({
        title: `Просмотр: ${displayName}`,
        content: (
          <div style={{ width: '100%', textAlign: 'center' }}>
            {document.fileType === 'photo' ? (
              <img src={url} alt={displayName} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            ) : (
              <iframe src={url} style={{ width: '100%', height: '70vh', border: 'none' }} title="File preview" />
            )}
          </div>
        ),
        width: 900,
        okText: 'Закрыть',
        afterClose: revoke,
      });
    } catch (e) {
      message.error('Не удалось открыть файл');
    }
  };

  const handleDownloadFinanceDocument = async (document: any) => {
    try {
      await downloadDocument(document.id, document);
    } catch (e) {
      message.error('Не удалось скачать файл');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <DollarOutlined /> Финансовый модуль
            </Title>
            <Text type="secondary">
              Финансовые очереди, риски и контроль оплат
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isLoadingStats || isLoadingApplications}
            >
              Обновить
            </Button>
          </Col>
        </Row>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <GovernanceRiskHeader compact title="⚠ Что блокирует финансовые процессы" />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'dashboard',
            label: 'Вторичная статистика',
            children: (
              <div>
                {/* Основные показатели */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Всего заявок"
                        value={financialStats.totalApplications}
                        valueStyle={{ color: '#1890ff' }}
                        prefix={<FileTextOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Завершенные"
                        value={financialStats.completedApplications}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Подписанные документы"
                        value={financialStats.signedDocsApplications}
                        valueStyle={{ color: '#722ed1' }}
                        prefix={<FileTextOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card>
                      <Statistic
                        title="Полученные платежи"
                        value={financialStats.receivedPaymentsApplications}
                        valueStyle={{ color: '#fa8c16' }}
                        prefix={<DollarOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Финансовая сводка */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12} lg={8}>
                    <Card>
                      <Statistic
                        title="Общая ставка клиентов"
                        value={financialStats.financialSummary.totalClientRate}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<DollarOutlined />}
                        suffix="₽"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card>
                      <Statistic
                        title="Общие затраты на маршруты"
                        value={financialStats.financialSummary.totalRoutesCost}
                        valueStyle={{ color: '#ff4d4f' }}
                        prefix={<DollarOutlined />}
                        suffix="₽"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card>
                      <Statistic
                        title="Общая прибыль"
                        value={financialStats.financialSummary.totalProfit}
                        valueStyle={{ color: getProfitColor(financialStats.financialSummary.totalProfit) }}
                        prefix={<DollarOutlined />}
                        suffix="₽"
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Прогресс прибыльности */}
                {financialStats.financialSummary.totalClientRate > 0 && (
                  <Card style={{ marginBottom: 24 }}>
                    <Title level={4} style={{ marginBottom: 16 }}>
                      Прогресс прибыльности
                    </Title>
                    <Row gutter={[16, 16]}>
                      <Col span={8}>
                        <Text>Доход</Text>
                        <Progress
                          percent={100}
                          strokeColor="#52c41a"
                          showInfo={false}
                          style={{ marginTop: 8 }}
                        />
                        <Text>
                          <MoneyCell value={financialStats.financialSummary.totalClientRate} type="income" />
                        </Text>
                      </Col>
                      <Col span={8}>
                        <Text>Затраты</Text>
                        <Progress
                          percent={(financialStats.financialSummary.totalRoutesCost / financialStats.financialSummary.totalClientRate) * 100}
                          strokeColor="#ff4d4f"
                          showInfo={false}
                          style={{ marginTop: 8 }}
                        />
                        <Text>
                          <MoneyCell value={financialStats.financialSummary.totalRoutesCost} type="expense" />
                        </Text>
                      </Col>
                      <Col span={8}>
                        <Text>Прибыль</Text>
                        <Progress
                          percent={(financialStats.financialSummary.totalProfit / financialStats.financialSummary.totalClientRate) * 100}
                          strokeColor={getProfitColor(financialStats.financialSummary.totalProfit)}
                          showInfo={false}
                          style={{ marginTop: 8 }}
                        />
                        <Text>
                          <MoneyCell
                            value={financialStats.financialSummary.totalProfit}
                            type={
                              financialStats.financialSummary.totalProfit > 0
                                ? 'income'
                                : financialStats.financialSummary.totalProfit < 0
                                  ? 'expense'
                                  : 'neutral'
                            }
                          />
                        </Text>
                      </Col>
                    </Row>
                  </Card>
                )}
              </div>
            ),
          },
          {
            key: 'company_applications',
            label: <span style={{ color: '#52c41a' }}>Клиентские заявки</span>,
            children: (
              <Card>
                {/* Фильтры */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={8}>
                    <Input
                      placeholder="Поиск по номеру заявки или клиенту..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      value={status}
                      onChange={setStatus}
                      style={{ width: '100%' }}
                      placeholder="Статус заявки"
                    >
                      <Option value="ALL">Все статусы</Option>
                      <Option value="CREATED">Создана</Option>
                      <Option value="IN_WORK">В работе</Option>
                      <Option value="COMPLETED">Завершена</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
                      Обновить
                    </Button>
                  </Col>
                </Row>

                {/* Сводка по счётчикам оплаты */}
                {(() => {
                  const companyApps = (applicationsData?.applications || []).filter((a: any) => isClientFlowApplication(a));
                  const awaitingPrepayment = companyApps.filter((a: any) => a.prepaymentStatus !== 'PAID' && a.prepaymentStartDate).length;
                  const awaitingMainPayment = companyApps.filter((a: any) => a.mainPaymentStatus !== 'PAID' && a.mainPaymentStartDate).length;
                  const overduePrepayment = companyApps.filter((a: any) => {
                    if (a.prepaymentStatus === 'PAID' || !a.prepaymentStartDate) return false;
                    const due = new Date(a.prepaymentStartDate).getTime() + (a.prepaymentDelayDays || 0) * 86400000;
                    return Date.now() > due;
                  }).length;
                  const overdueMain = companyApps.filter((a: any) => {
                    if (a.mainPaymentStatus === 'PAID' || !a.mainPaymentStartDate) return false;
                    const due = new Date(a.mainPaymentStartDate).getTime() + (a.mainPaymentDelayDays || 0) * 86400000;
                    return Date.now() > due;
                  }).length;

                  return (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                      <Col span={6}>
                        <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                          <Statistic title="Всего заявок (клиент)" value={companyApps.length} valueStyle={{ color: '#52c41a' }} />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                          <Statistic title="Ожидают предоплату" value={awaitingPrepayment} valueStyle={{ color: '#1890ff' }} />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small" style={{ backgroundColor: '#fffbe6' }}>
                          <Statistic title="Ожидают доплату" value={awaitingMainPayment} valueStyle={{ color: '#faad14' }} />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card size="small" style={{ backgroundColor: overduePrepayment + overdueMain > 0 ? '#fff1f0' : '#fafafa' }}>
                          <Statistic 
                            title="Просрочено" 
                            value={overduePrepayment + overdueMain} 
                            valueStyle={{ color: overduePrepayment + overdueMain > 0 ? '#ff4d4f' : '#8c8c8c' }} 
                          />
                        </Card>
                      </Col>
                    </Row>
                  );
                })()}

                {isLoadingApplications ? (
                  <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
                ) : (
                  <DataTable
                    columns={companyApplicationsColumns}
                    dataSource={(applicationsData?.applications || []).filter((a: any) => {
                      if (!isClientFlowApplication(a)) return false;
                      if (status !== 'ALL' && a.status !== status) return false;
                      return true;
                    })}
                    pagination={{
                      total: (applicationsData?.applications || []).filter((a: any) => isClientFlowApplication(a)).length,
                      current: page,
                      pageSize,
                      showSizeChanger: true,
                      onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    }}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'partner_applications',
            label: <span>Партнёрские заявки</span>,
            children: (
              <Card>
                {/* Фильтры */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={8}>
                    <Input
                      placeholder="Поиск по номеру заявки или клиенту..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      value={status}
                      onChange={setStatus}
                      style={{ width: '100%' }}
                      placeholder="Статус заявки"
                    >
                      <Option value="ALL">Все статусы</Option>
                      <Option value="CREATED">Создана</Option>
                      <Option value="IN_WORK">В работе</Option>
                      <Option value="COMPLETED">Завершена</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh}>
                      Обновить
                    </Button>
                  </Col>
                </Row>

                {isLoadingApplications ? (
                  <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
                ) : (
                  <Tabs
                    items={[
                      {
                        key: 'partner_work',
                        label: 'В работе',
                        children: (
                          <DataTable
                            columns={partnerApplicationsColumns}
                            dataSource={(applicationsData?.applications || []).filter((a: any) => 
                              isPartnerBasisApplication(a) && (a.status === 'IN_WORK' || a.status === 'CREATED')
                            )}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                          />
                        )
                      },
                      {
                        key: 'partner_wait',
                        label: 'Ждут оплаты',
                        children: (
                          <DataTable
                            columns={partnerApplicationsColumns}
                            dataSource={(applicationsData?.applications || []).filter((a: any) => 
                              isPartnerBasisApplication(a) && a.status === 'IN_WORK' && 
                              a.primaryDocsStatus === 'SIGNED' && a.partnerPaymentStatus !== 'RECEIVED'
                            )}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                          />
                        )
                      },
                      {
                        key: 'partner_completed',
                        label: 'Выполненные',
                        children: (
                          <DataTable
                            columns={partnerApplicationsColumns}
                            dataSource={(applicationsData?.applications || []).filter((a: any) => 
                              isPartnerBasisApplication(a) && a.status === 'COMPLETED'
                            )}
                            pagination={false}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                          />
                        )
                      }
                    ]}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'routes',
            label: 'Маршруты',
            children: (
              <Card>
                {/* Фильтры для маршрутов */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={4}>
                    <Input
                      placeholder="Поиск по ID маршрута, заявке или перевозчику..."
                      value={routesSearch}
                      onChange={(e) => setRoutesSearch(e.target.value)}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesResponsibleLogistId}
                      onChange={setRoutesResponsibleLogistId}
                      style={{ width: '100%' }}
                      placeholder="Логист"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {filterResponsibleEligibleUsers(usersData).map((u: any) => {
                        const label = formatResponsibleUserLabel(u);
                        return (
                          <Option key={u.id} value={u.id} label={label}>
                            {label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesCarrierId}
                      onChange={setRoutesCarrierId}
                      style={{ width: '100%' }}
                      placeholder="Перевозчик"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                    >
                      {(carriersData || []).map((c: any) => (
                        <Option key={c.id} value={c.id}>{c.name}</Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesApplication}
                      onChange={setRoutesApplication}
                      style={{ width: '100%' }}
                      placeholder="Заявка"
                      allowClear
                    >
                      <Option value="ALL">Все заявки</Option>
                      {applicationsData?.applications?.map((app: any) => (
                        <Option key={app.id} value={app.internalNumber}>
                          {app.internalNumber}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesStatus}
                      onChange={setRoutesStatus}
                      style={{ width: '100%' }}
                      placeholder="Статус маршрута"
                    >
                      <Option value="ALL">Все статусы</Option>
                      <Option value="ACTIVE">Активный</Option>
                      <Option value="COMPLETED">Завершен</Option>
                      <Option value="CANCELLED">Отменен</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesTransportType}
                      onChange={setRoutesTransportType}
                      style={{ width: '100%' }}
                      placeholder="Тип транспорта"
                    >
                      <Option value="ALL">Все типы</Option>
                      <Option value="AUTO">Авто</Option>
                      <Option value="RAILWAY">ЖД</Option>
                      <Option value="AVIATION">Авиа</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Select
                      value={routesCompanyOnly}
                      onChange={setRoutesCompanyOnly}
                      style={{ width: '100%' }}
                      placeholder="Тип маршрута"
                    >
                      <Option value={false}>Все маршруты</Option>
                      <Option value={true}>Только свои</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={4}>
                    <Button 
                      type="primary" 
                      icon={<ReloadOutlined />}
                      onClick={handleRefresh}
                    >
                      Обновить
                    </Button>
                  </Col>
                </Row>

                {isLoadingRoutes ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin />
                  </div>
                ) : (
                  <DataTable
                    columns={routesColumns}
                    dataSource={routesData?.routes || []}
                    sticky
                    pagination={{
                      current: routesPage,
                      pageSize: 20,
                      total: routesData?.pagination.total || 0,
                      onChange: setRoutesPage,
                      showSizeChanger: false,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} из ${total} записей`,
                    }}
                    rowKey="id"
                    onRow={(record: any) => ({
                      onClick: () => {
                        setRouteDetailsId(record.id);
                        setIsRouteDetailsVisible(true);
                      },
                      style: { cursor: 'pointer' }
                    })}
                    // Внутренний скролл таблицы: горизонтальный доступен всегда, не нужно прокручивать страницу вниз
                    scroll={{ x: 'max-content', y: routesTableY }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'logists',
            label: 'Логисты',
            children: (
              <Tabs defaultActiveKey="logist-routes" type="card">
                <Tabs.TabPane tab="Маршруты" key="logist-routes">
                  <Card>
                    {/* Выбор логиста */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Select
                          value={selectedLogistId}
                          onChange={(value) => setSelectedLogistId(value)}
                          style={{ width: '100%' }}
                          placeholder="Выберите логиста"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                        >
                          {filterResponsibleEligibleUsers(usersData).map((u: any) => {
                            const label = formatResponsibleUserLabel(u);
                            return (
                              <Option key={u.id} value={u.id} label={label}>
                                {label}
                              </Option>
                            );
                          })}
                        </Select>
                      </Col>
                    </Row>

                    {!selectedLogistId ? (
                      <Text type="secondary">
                        Выберите логиста, чтобы увидеть его маршруты.
                      </Text>
                    ) : isLoadingLogistsRoutes ? (
                      <div style={{ textAlign: 'center', padding: 20 }}>
                        <Spin />
                      </div>
                    ) : (
                      <DataTable
                        columns={logistRoutesColumns}
                        dataSource={logistsRoutesData?.routes || []}
                        sticky
                        pagination={false}
                        rowKey="id"
                        onRow={(record: any) => ({
                          onClick: () => {
                            setRouteDetailsId(record.id);
                            setIsRouteDetailsVisible(true);
                          },
                          style: { cursor: 'pointer' },
                        })}
                        scroll={{ x: 'max-content', y: routesTableY }}
                      />
                    )}
                  </Card>
                </Tabs.TabPane>
                <Tabs.TabPane tab="Заявки" key="logist-applications">
                  <Card>
                    <Text type="secondary">
                      Вкладка «Заявки» для логистов пока в разработке. Мы добавим таблицу позже.
                    </Text>
                  </Card>
                </Tabs.TabPane>
              </Tabs>
            ),
          },
          {
            key: 'payments',
            label: 'Платежи',
            children: (
              <Card>
                {/* Фильтры для платежей */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      value={paymentsStatus}
                      onChange={setPaymentsStatus}
                      style={{ width: '100%' }}
                      placeholder="Статус платежа"
                    >
                      <Option value="ALL">Все статусы</Option>
                      <Option value="PENDING">Ожидает</Option>
                      <Option value="PARTIAL">Частично</Option>
                      <Option value="PAID">Оплачен</Option>
                      <Option value="OVERDUE">Просрочен</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      value={paymentsType}
                      onChange={setPaymentsType}
                      style={{ width: '100%' }}
                      placeholder="Тип платежа"
                    >
                      <Option value="ALL">Все типы</Option>
                      <Option value="PREPAYMENT">Предоплата</Option>
                      <Option value="FINAL_PAYMENT">Доплата</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      value={paymentsCompanyOnly}
                      onChange={setPaymentsCompanyOnly}
                      style={{ width: '100%' }}
                      placeholder="Тип маршрута"
                    >
                      <Option value={false}>Все маршруты</Option>
                      <Option value={true}>Только свои</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={handleCreatePayment}
                    >
                      Создать платеж
                    </Button>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={() => refetchPayments()}
                      loading={isLoadingPayments}
                    >
                      Обновить
                    </Button>
                  </Col>
                </Row>

                {isLoadingPayments ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin />
                  </div>
                ) : (
                  <DataTable
                    columns={paymentsColumns}
                    dataSource={paymentsData?.payments || []}
                    pagination={{
                      current: paymentsPage,
                      pageSize: 20,
                      total: paymentsData?.pagination.total || 0,
                      onChange: setPaymentsPage,
                      showSizeChanger: false,
                      showQuickJumper: true,
                      showTotal: (total, range) => 
                        `${range[0]}-${range[1]} из ${total} записей`,
                    }}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'documents',
            label: 'Документы',
            children: (
              <Card>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={6}>
                    <Button 
                      type="primary" 
                      icon={<UploadOutlined />}
                      onClick={() => {
                        setSelectedRouteForUpload(null);
                        setIsUploadLoadingDocsModalVisible(true);
                      }}
                    >
                      Загрузить документы погрузки
                    </Button>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Button 
                      type="primary" 
                      icon={<UploadOutlined />}
                      onClick={() => {
                        setSelectedRouteForUpload(null);
                        setIsUploadTransportDocsModalVisible(true);
                      }}
                    >
                      Загрузить ТН
                    </Button>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={refetchDocuments}
                      loading={isLoadingDocuments}
                    >
                      Обновить
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} lg={6}>
                    <Select
                      showSearch
                      placeholder="Маршрут для отображения документов"
                      style={{ width: '100%' }}
                      value={documentsRouteId ?? undefined}
                      onChange={(val) => setDocumentsRouteId(val)}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {(routesData?.routes || []).map((r: any) => (
                        <Option key={r.id} value={r.id}>
                          №{r.id} — {r.transportType}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>

                {/* Фильтры документов */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} lg={6}>
                    <Select
                      placeholder="Тип документа"
                      style={{ width: '100%' }}
                      allowClear
                      onChange={(val) => {
                        // фильтрация по типу документа (photo/file)
                        // применяем на клиенте
                        // client-side filter placeholder (no-op) — kept for future use
                        void (routeDocuments || []).filter((d: any) => !val || d.fileType === val);
                        // перезапишем локальные выборки
                        // напрямик локальные массивы менять нельзя, поэтому используем вычисления в рендере табов
                        // оставляем как справочный селектор
                      }}
                    >
                      <Option value="photo">Фото</Option>
                      <Option value="file">Документ</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <RangePicker
                      style={{ width: '100%' }}
                      placeholder={["Дата от", "Дата до"]}
                      onChange={(dates) => {
                        // фильтрация по createdAt, клиентская
                        // оставляем как справочный контрол — фильтрацию выполняем прямо в dataSource ниже через вычисление
                        (window as any).__financeDocsDateFilter = dates
                          ? {
                              from: dates[0]?.startOf('day'),
                              to: dates[1]?.endOf('day'),
                            }
                          : undefined;
                      }}
                    />
                  </Col>
                </Row>

                <Tabs defaultActiveKey="loading">
                  <Tabs.TabPane key="loading" tab={`Документы погрузки (${(loadingDocs?.length || 0)})`}>
                    <DataTable
                      columns={loadingDocumentsColumns}
                      dataSource={(loadingDocs || []).filter((d: any) => {
                        const df = (window as any).__financeDocsDateFilter;
                        if (!df) return true;
                        const created = dayjs(d.createdAt);
                        return created.valueOf() >= df.from.valueOf() && created.valueOf() <= df.to.valueOf();
                      })}
                      loading={isFetchingRouteDocuments || isLoadingDocuments}
                      pagination={false}
                      rowKey="id"
                      locale={{ emptyText: 'Нет документов погрузки' }}
                    />
                  </Tabs.TabPane>
                  <Tabs.TabPane key="transport" tab={`Транспортные документы (${(transportDocs?.length || 0)})`}>
                    <DataTable
                      columns={transportDocumentsColumns}
                      dataSource={(transportDocs || []).filter((d: any) => {
                        const df = (window as any).__financeDocsDateFilter;
                        if (!df) return true;
                        const created = dayjs(d.createdAt);
                        return created.valueOf() >= df.from.valueOf() && created.valueOf() <= df.to.valueOf();
                      })}
                      loading={isFetchingRouteDocuments || isLoadingDocuments}
                      pagination={false}
                      rowKey="id"
                      locale={{ emptyText: 'Нет транспортных документов' }}
                    />
                  </Tabs.TabPane>
                </Tabs>
              </Card>
            ),
          },
        ]}
      />

      {/* Модальное окно для обновления заявки */}
      <Modal
        title="Обновить данные заявки"
        open={isUpdateModalVisible}
        onCancel={() => {
          setIsUpdateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSubmit}
        >
          <Form.Item
            name="primaryDocsStatus"
            label="Статус первичных документов"
            initialValue={'NOT_SIGNED'}
            rules={[{ required: true, message: 'Выберите статус первичных документов' }]}
          >
            <Select>
              <Option value="NOT_SIGNED">Не подписаны</Option>
              <Option value="SIGNED">Подписаны</Option>
            </Select>
          </Form.Item>

          {/* Для партнёрских заявок - платёж от партнёра */}
          {isPartnerBasisApplication(selectedApplication) && (
            <>
              <Form.Item
                name="partnerPaymentStatus"
                label="Статус платежа от партнёра"
              >
                <Select>
                  <Option value="NOT_RECEIVED">Не получен</Option>
                  <Option value="RECEIVED">Получен</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="partnerPaymentAmount"
                label="Сумма платежа от партнёра"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Введите сумму"
                />
              </Form.Item>

              <Form.Item
                name="partnerReceived"
                label="Получено от партнёра"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Сумма фактически полученная"
                />
              </Form.Item>

              <Form.Item
                name="partnerPaymentDate"
                label="Дата платежа от партнёра"
              >
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>

              <Form.Item
                name="partnerPaymentInvoice"
                label="Номер счёта партнёра"
              >
                <Input placeholder="№ счета или примечание" />
              </Form.Item>
            </>
          )}

          {/* Для своих заявок - платёж от клиента */}
          {isClientFlowApplication(selectedApplication) && (
            <>
              {/* Предоплата */}
              {(selectedApplication?.prepaymentPercent || 0) > 0 && (
                <Card size="small" title="Предоплата от клиента" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="prepaymentReceivedAmount"
                        label="Получено (руб.)"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Сумма"
                          min={0}
                          precision={2}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="prepaymentReceivedDate"
                        label="Дата оплаты"
                      >
                        <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="prepaymentStatus"
                    label="Статус"
                    style={{ marginBottom: 0 }}
                  >
                    <Select>
                      <Option value="NOT_PAID">Не оплачена</Option>
                      <Option value="PARTIAL">Частично оплачена</Option>
                      <Option value="PAID">Оплачена полностью</Option>
                    </Select>
                  </Form.Item>
                </Card>
              )}

              {/* Доплата */}
              {(100 - (selectedApplication?.prepaymentPercent || 0)) > 0 && (
                <Card size="small" title="Доплата от клиента" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="mainPaymentReceivedAmount"
                        label="Получено (руб.)"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="Сумма"
                          min={0}
                          precision={2}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="mainPaymentReceivedDate"
                        label="Дата оплаты"
                      >
                        <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="mainPaymentStatus"
                    label="Статус"
                    style={{ marginBottom: 0 }}
                  >
                    <Select>
                      <Option value="NOT_PAID">Не оплачена</Option>
                      <Option value="PARTIAL">Частично оплачена</Option>
                      <Option value="PAID">Оплачена полностью</Option>
                    </Select>
                  </Form.Item>
                </Card>
              )}

              <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                💡 Счётчики отсрочки запускаются из таблицы клиентских заявок.
              </Text>
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
              <Button onClick={() => {
                setIsUpdateModalVisible(false);
                form.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Боковая панель: маршруты по выбранной заявке */}
      <Drawer
        title={`Маршруты по заявке ${applicationFinanceDetail?.application?.internalNumber || applicationInternalNumberForRoutes || ''}`}
        open={isRoutesDrawerOpen}
        width={980}
        onClose={() => {
          setIsRoutesDrawerOpen(false);
          setApplicationIdForRoutes(null);
          setApplicationInternalNumberForRoutes(null);
        }}
        destroyOnClose
      >
        {isFetchingApplicationDetail || isLoadingRoutesByApplication ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <DataTable
            size="small"
            bordered
            rowKey="id"
            pagination={false}
            sticky
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
              { 
                title: 'Тип', 
                dataIndex: 'transportType', 
                key: 'transportType',
                width: 80,
                render: (t: string) => (
                  <Tag color="blue">{t === 'AUTO' ? 'Авто' : t === 'RAILWAY' ? 'ЖД' : t === 'AVIATION' ? 'Авиа' : t}</Tag>
                )
              },
              { 
                title: 'Маршрут', 
                key: 'route',
                width: 320,
                render: (_: any, r: any) => (
                  <div style={{ color: 'var(--text)' }}>
                    <div>{r.loadingAddress || 'Не указано'}</div>
                    <div style={{ color: 'var(--text-secondary)' }}>→ {r.unloadingAddress || 'Не указано'}</div>
                  </div>
                )
              },
              { title: 'Ставка', dataIndex: 'totalAmount', key: 'totalAmount', width: 110, align: 'right', render: (v: number) => (
                <Text strong>
                  <MoneyCell value={v || 0} type="neutral" />
                </Text>
              ) },
              { title: 'Предоплата', key: 'pre', width: 110, align: 'right', render: (_: any, r: any) => (
                <MoneyCell value={r.prepaymentAmount || 0} type="neutral" />
              ) },
              { title: 'Доплата', key: 'final', width: 110, align: 'right', render: (_: any, r: any) => (
                <MoneyCell value={r.remainingAmount || 0} type="neutral" />
              ) },
              { title: 'Оплачено', key: 'paid', width: 110, align: 'right', render: (_: any, r: any) => {
                  const paid = (r.payments || []).reduce((s: number, p: any) => s + Number(p.amountPaid || 0), 0);
                  return paid > 0 ? (
                    <MoneyCell value={paid} type="income" />
                  ) : (
                    <Text type="secondary">
                      <MoneyCell value={paid} type="neutral" />
                    </Text>
                  );
                }
              },
              { title: 'Статус', dataIndex: 'status', key: 'status', width: 100, render: (s: string) => {
                  const u = String(s || '').toUpperCase();
                  if (u === 'COMPLETED' || u === 'CANCELLED') return <StatusCell status={u} />;
                  return <StatusCell status="IN_PROGRESS" />;
                }
              },
            ]}
            dataSource={(applicationFinanceDetail?.routes && applicationFinanceDetail.routes.length > 0)
              ? applicationFinanceDetail.routes
              : (routesByApplication?.routes || [])}
            footer={() => {
              const routes = (applicationFinanceDetail?.routes && applicationFinanceDetail.routes.length > 0)
                ? applicationFinanceDetail.routes
                : (routesByApplication?.routes || []);
              const total = routes.reduce((s: number, r: any) => s + (r.totalAmount || 0), 0);
              const pre = routes.reduce((s: number, r: any) => s + (r.prepaymentAmount || 0), 0);
              const fin = routes.reduce((s: number, r: any) => s + (r.remainingAmount || 0), 0);
              const paid = routes.reduce((s: number, r: any) => s + (r.payments || []).reduce((a: number, p: any) => a + Number(p.amountPaid || 0), 0), 0);
              return (
                <Row gutter={16}>
                  <Col>
                    <Text strong>
                      Итого стоимость: <MoneyCell value={total} type="neutral" />
                    </Text>
                  </Col>
                  <Col>
                    <Text strong>
                      Итого предоплата: <MoneyCell value={pre} type="neutral" />
                    </Text>
                  </Col>
                  <Col>
                    <Text strong>
                      Итого доплата: <MoneyCell value={fin} type="neutral" />
                    </Text>
                  </Col>
                  <Col>
                    <Text strong>
                      Оплачено: <MoneyCell value={paid} type="income" />
                    </Text>
                  </Col>
                </Row>
              );
            }}
          />
        )}
      </Drawer>

      {/* Модальное окно для создания платежа */}
      <Modal
        title="Создать новый платеж"
        open={isCreatePaymentModalVisible}
        onCancel={() => {
          setIsCreatePaymentModalVisible(false);
          paymentForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handleCreatePaymentSubmit}
        >
          <Form.Item
            name="routeId"
            label="Маршрут"
            rules={[{ required: true, message: 'Выберите маршрут' }]}
          >
            <Select 
              placeholder="Выберите маршрут"
              loading={isLoadingAvailableRoutes}
              showSearch
              optionLabelProp="label"
              onChange={() => {
                paymentForm.setFieldsValue({ obligationId: undefined });
              }}
              filterOption={(input, option) => (((option?.label as unknown as string) || '')
                .toLowerCase()
                .includes(input.toLowerCase()))}
            >
              {availableRoutes?.map(route => {
                const routeLabel = `№${route.id} - ${route.transportType === 'AUTO' ? 'Авто' : 
                  route.transportType === 'RAILWAY' ? 'ЖД' : 
                  route.transportType === 'AVIATION' ? 'Авиа' : route.transportType}`;
                
                return (
                  <Option 
                    key={route.id} 
                    value={route.id}
                    label={`${routeLabel}`}
                    disabled={!route.hasLoadingDocs}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{routeLabel}</span>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>№{route.id}</div>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: 2 }}>
                        {route.applications?.[0]?.client?.name ? `Клиент: ${route.applications[0].client.name}` : 'Клиент: —'}
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {!route.hasLoadingDocs && <Tag color="red" style={{ fontSize: '10px' }}>Нет счета и фото</Tag>}
                        {!route.hasTransportDocs && (
                          <Tag color="orange" style={{ fontSize: '10px' }}>Нет ТН</Tag>
                        )}
                        {route.isPrepaymentClosed && <Tag color="green" style={{ fontSize: '10px' }}>Предоплата закрыта</Tag>}
                        {route.isFinalClosed && <Tag color="green" style={{ fontSize: '10px' }}>Доплата закрыта</Tag>}
                        {route.existingPayments > 0 && <Tag color="blue" style={{ fontSize: '10px' }}>{route.existingPayments} платежей</Tag>}
                      </div>
                      {(route.prepaymentRequired != null || route.finalRequired != null) && (
                        <div style={{ marginTop: 6, fontSize: '12px', color: 'var(--text-secondary)' }}>
                          <div>
                            Остаток предоплаты:{' '}
                            <MoneyCell
                              value={route.prepaymentDebt || 0}
                              type={(route.prepaymentDebt || 0) > 0 ? 'expense' : 'income'}
                            />
                          </div>
                          <div>
                            Остаток доплаты:{' '}
                            <MoneyCell
                              value={route.finalDebt || 0}
                              type={(route.finalDebt || 0) > 0 ? 'expense' : 'income'}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="paymentObligationDirection"
            label="Направление платежа"
            rules={[{ required: true, message: 'Выберите направление' }]}
          >
            <Select
              placeholder="Доход или расход"
              onChange={() => paymentForm.setFieldsValue({ obligationId: undefined })}
              disabled={!routeIdForObligations}
            >
              <Option value="INCOME">Получить от клиента (доход)</Option>
              <Option value="EXPENSE">Оплатить перевозчику (расход)</Option>
            </Select>
          </Form.Item>

          {routeIdForObligations && paymentObligationDirection && routeObligationsFiltered.length > 0 && (
            <Form.Item
              name="obligationId"
              label="Обязательство"
              rules={[{ required: true, message: 'Выберите обязательство' }]}
            >
              <Select
                placeholder="Выберите обязательство"
                optionFilterProp="label"
                onChange={(val) => {
                  if (val) {
                    const obl = routeObligationsFiltered.find((o: any) => o.id === val);
                    if (obl) {
                      paymentForm.setFieldsValue({
                        amount: obl.remainingAmount ?? obl.amountWithVAT,
                      });
                    }
                  }
                }}
              >
                {routeObligationsFiltered.map((obl: any) => (
                  <Option
                    key={obl.id}
                    value={obl.id}
                    label={`${obligationStageLabel(obl.stage)} · ${financeDirectionLabel(obl.direction)} — ${formatCurrency(obl.remainingAmount ?? obl.amountWithVAT)} (срок ${obl.dueDate ? dayjs(obl.dueDate).format('DD.MM') : '—'})`}
                  >
                    {`${obligationStageLabel(obl.stage)} · ${financeDirectionLabel(obl.direction)} — ${formatCurrency(obl.remainingAmount ?? obl.amountWithVAT)} (срок ${obl.dueDate ? dayjs(obl.dueDate).format('DD.MM') : '—'})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {routeIdForObligations && paymentObligationDirection && routeObligationsFiltered.length === 0 && (
            <div style={{ marginBottom: 12, color: '#999', fontSize: 12 }}>
              Нет открытых обязательств с остатком для выбранного направления.
            </div>
          )}

          <Form.Item shouldUpdate={(prev, curr) => prev.routeId !== curr.routeId || prev.obligationId !== curr.obligationId || prev.paymentObligationDirection !== curr.paymentObligationDirection}>
            {({ getFieldValue }) => {
              const oblId = getFieldValue('obligationId');
              const obl = oblId ? routeObligations.find((o: any) => o.id === oblId) : null;
              const limitFromObl = obl ? (obl.remainingAmount ?? obl.amountWithVAT) : 0;
              return (
                <>
                  <Form.Item
                    name="amount"
                    label={`Сумма${limitFromObl ? ` (остаток по обязательству: ${formatCurrency(limitFromObl)})` : ''}`}
                    rules={[{ required: true, message: 'Введите сумму' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                      parser={(v) => (v ? Number(String(v).replace(/\$\s?|(,*)/g, '')) : 0)}
                      placeholder="Введите сумму"
                      max={limitFromObl || undefined}
                    />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Дата платежа"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Выберите дату"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Создать
              </Button>
              <Button type="primary" onClick={handleCreateAndProcessPayment}>
                Провести платеж
              </Button>
              <Button onClick={() => {
                setIsCreatePaymentModalVisible(false);
                paymentForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для редактирования платежа */}
      <Modal
        title="Редактировать платеж"
        open={isUpdatePaymentModalVisible}
        onCancel={() => {
          setIsUpdatePaymentModalVisible(false);
          updatePaymentForm.resetFields();
          setSelectedPayment(null);
        }}
        footer={null}
      >
        <Form
          form={updatePaymentForm}
          layout="vertical"
          onFinish={handleUpdatePaymentSubmit}
        >
          <Form.Item
            name="amountPaid"
            label="Оплаченная сумма"
            rules={[{ required: true, message: 'Введите оплаченную сумму' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="Введите оплаченную сумму"
            />
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Дата платежа"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Выберите дату"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Сохранить
              </Button>
              <Button onClick={() => {
                setIsUpdatePaymentModalVisible(false);
                updatePaymentForm.resetFields();
                setSelectedPayment(null);
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для загрузки документов погрузки */}
      <Modal
        title="Загрузить документы погрузки"
        open={isUploadLoadingDocsModalVisible}
        onCancel={() => {
          setIsUploadLoadingDocsModalVisible(false);
          setSelectedRouteForUpload(null);
        }}
        footer={null}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="Маршрут" required>
            <Select 
              placeholder="Выберите маршрут"
              value={selectedRouteForUpload?.id}
              disabled={!!selectedRouteForUpload}
            >
              {availableRoutes?.map(route => (
                <Option key={route.id} value={route.id}>
                  №{route.id} - {route.transportType === 'AUTO' ? 'Авто' : 
                    route.transportType === 'RAILWAY' ? 'ЖД' : 
                    route.transportType === 'AVIATION' ? 'Авиа' : route.transportType}
                </Option>
              ))}
            </Select>
            {selectedRouteForUpload && (
              <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
                Выбран маршрут: №{selectedRouteForUpload.id}
              </Text>
            )}
          </Form.Item>
          
          <Form.Item label="Дата погрузки" required>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item label="Фото погрузки">
            <Input id="finance-loading-photos" type="file" multiple accept="image/*" />
            <Text type="secondary">Выберите фотографии процесса погрузки</Text>
          </Form.Item>
          
          <Form.Item label="Счет">
            <Input id="finance-loading-invoice" type="file" accept=".pdf,.doc,.docx" />
            <Text type="secondary">Загрузите счет за погрузку</Text>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                onClick={() => {
                  const photosInput = document.getElementById('finance-loading-photos') as HTMLInputElement | null;
                  const invoiceInput = document.getElementById('finance-loading-invoice') as HTMLInputElement | null;
                  const files = new DataTransfer();
                  photosInput?.files && Array.from(photosInput.files).forEach(f => files.items.add(f));
                  invoiceInput?.files && Array.from(invoiceInput.files).forEach(f => files.items.add(f));
                  handleUploadLoadingDocuments(files.files);
                }}
                loading={isLoadingDocuments}
              >
                Загрузить
              </Button>
              <Button onClick={() => {
                setIsUploadLoadingDocsModalVisible(false);
                setSelectedRouteForUpload(null);
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для загрузки транспортных документов */}
      <Modal
        title="Загрузить транспортные документы"
        open={isUploadTransportDocsModalVisible}
        onCancel={() => {
          setIsUploadTransportDocsModalVisible(false);
          setSelectedRouteForUpload(null);
        }}
        footer={null}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="Маршрут" required>
            <Select 
              placeholder="Выберите маршрут"
              value={selectedRouteForUpload?.id}
              disabled={!!selectedRouteForUpload}
            >
              {availableRoutes?.map(route => (
                <Option key={route.id} value={route.id}>
                  №{route.id} - {route.transportType === 'AUTO' ? 'Авто' : 
                    route.transportType === 'RAILWAY' ? 'ЖД' : 
                    route.transportType === 'AVIATION' ? 'Авиа' : route.transportType}
                </Option>
              ))}
            </Select>
            {selectedRouteForUpload && (
              <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
                Выбран маршрут: №{selectedRouteForUpload.id}
              </Text>
            )}
          </Form.Item>
          
          <Form.Item label="Тип доставки" required>
            <Select placeholder="Выберите тип доставки">
              <Option value="MAIL">Почта</Option>
              <Option value="EDO">ЭДО</Option>
              <Option value="PERSONAL">Лично</Option>
            </Select>
          </Form.Item>
          
          <Form.Item label="Номер отслеживания">
            <Input placeholder="Введите номер отслеживания" />
          </Form.Item>
          
          <Form.Item label="Дата получения">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item label="Транспортные накладные">
            <Input id="finance-transport-docs" type="file" multiple accept=".pdf,.doc,.docx" />
            <Text type="secondary">Загрузите оригиналы транспортных накладных</Text>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                onClick={() => {
                  const docsInput = document.getElementById('finance-transport-docs') as HTMLInputElement | null;
                  const files = docsInput?.files || new DataTransfer().files;
                  handleUploadTransportDocuments(files);
                }}
                loading={isLoadingDocuments}
              >
                Загрузить
              </Button>
              <Button onClick={() => {
                setIsUploadTransportDocsModalVisible(false);
                setSelectedRouteForUpload(null);
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* UploadDocumentModal reused for application uploads */}
      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => {
          refetchApplications();
          setUploadModalVisible(false);
          setUploadModalApplication(null);
          setUploadModalRoute(null);
        }}
        onSuccess={() => {
          refetchApplications();
          setUploadModalVisible(false);
          setUploadModalApplication(null);
          setUploadModalRoute(null);
        }}
        application={uploadModalApplication || undefined}
        route={uploadModalRoute || undefined}
        isPrimaryDocs={Boolean(uploadModalApplication)}
      />

      {/* Модальное окно для редактирования дохода маршрута */}
      <Modal
        title="Редактировать доход маршрута"
        open={isEditRouteIncomeModalVisible}
        onCancel={() => {
          setIsEditRouteIncomeModalVisible(false);
          setSelectedRouteForIncomeEdit(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={incomeForm}
          layout="vertical"
          onFinish={handleUpdateRouteIncome}
        >
          <Form.Item label="Маршрут" required>
            <Text strong>
              №{selectedRouteForIncomeEdit?.id} - {selectedRouteForIncomeEdit?.transportType === 'AUTO' ? 'Авто' : 
                selectedRouteForIncomeEdit?.transportType === 'RAILWAY' ? 'ЖД' : 
                selectedRouteForIncomeEdit?.transportType === 'AVIATION' ? 'Авиа' : selectedRouteForIncomeEdit?.transportType}
            </Text>
          </Form.Item>
          
          <Form.Item 
            label="Доход" 
            name="income"
            rules={[{ required: true, message: 'Введите доход' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Введите доход в рублях"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number(value?.replace(/\$\s?|(,*)/g, '') || 0) as any}
              min={0}
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={false}
              >
                Сохранить
              </Button>
              <Button onClick={() => {
                setIsEditRouteIncomeModalVisible(false);
                setSelectedRouteForIncomeEdit(null);
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно запуска счётчика оплаты */}
      <Modal
        title={counterModalType === 'prepayment' ? 'Запуск счётчика предоплаты' : 'Запуск счётчика доплаты'}
        open={counterModalVisible}
        onOk={handleCounterModalSave}
        onCancel={() => setCounterModalVisible(false)}
        okText="Запустить"
        cancelText="Отмена"
        width={500}
      >
        <Form form={counterForm} layout="vertical">
          <Form.Item
            name="condition"
            label="Условие запуска"
            rules={[{ required: true, message: 'Выберите условие' }]}
          >
            <Select placeholder="Выберите условие">
              {counterModalType === 'prepayment' ? (
                <>
                  <Option value="INVOICE_RECEIVED">После получения счёта</Option>
                  <Option value="ON_LOADING">По факту погрузки</Option>
                  <Option value="OTHER">Другое условие</Option>
                </>
              ) : (
                <>
                  <Option value="ON_UNLOADING">По факту выгрузки</Option>
                  <Option value="ACT_RECEIVED">По факту получения клиентом Акта выполненных работ</Option>
                  <Option value="ORIGINALS_RECEIVED">По факту получения клиентом оригиналов Документов</Option>
                  <Option value="OTHER">Другое условие</Option>
                </>
              )}
            </Select>
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Дата начала отсчёта"
            rules={[{ required: true, message: 'Укажите дату' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.condition !== currentValues.condition}
          >
            {({ getFieldValue }) => {
              const condition = getFieldValue('condition');
              if (condition === 'INVOICE_RECEIVED') {
                return (
                  <Form.Item
                    name="invoiceNumber"
                    label="Номер счёта"
                    rules={[{ required: true, message: 'Введите номер счёта' }]}
                  >
                    <Input placeholder="Введите номер счёта" />
                  </Form.Item>
                );
              }
              if (condition === 'OTHER') {
                return (
                  <Form.Item
                    name="otherCondition"
                    label="Описание условия"
                    rules={[{ required: true, message: 'Опишите условие' }]}
                  >
                    <Input.TextArea rows={2} placeholder="Опишите условие запуска счётчика" />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {counterModalType === 'prepayment' 
              ? 'Счётчик отсрочки начнёт отсчёт с указанной даты. Клиент должен оплатить предоплату до истечения срока.'
              : 'Счётчик отсрочки начнёт отсчёт с указанной даты. Клиент должен оплатить остаток до истечения срока.'
            }
          </Text>
        </Form>
      </Modal>

      {/* Деталка маршрута по клику (Финансы → Маршруты) */}
      <Modal
        title="Загрузка маршрута…"
        open={isRouteDetailsVisible && !routeDetailsData && isFetchingRouteDetails}
        footer={null}
        onCancel={() => {
          setIsRouteDetailsVisible(false);
          setRouteDetailsId(null);
        }}
        width={520}
      >
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      </Modal>

      <RouteDetailsModal
        visible={isRouteDetailsVisible && !!routeDetailsData}
        route={(routeDetailsData as any) || null}
        onClose={() => {
          setIsRouteDetailsVisible(false);
          setRouteDetailsId(null);
        }}
      />
    </div>
  );
};

console.log('FINANCE PAGE SWITCHED TO READ MODEL');

export default FinancePage; 